/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import cn from 'classnames';
import useSWR from 'swr';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
// Sidebar (a 1-D vertical list) uses framer-motion Reorder to match the group list
// on the main tab. The content grid stays on @dnd-kit above because framer Reorder
// is single-axis and cannot reflow the 2-D (rectSortingStrategy) grid.
import { Reorder, useDragControls } from 'framer-motion';
import { AppContext, AuthContext } from 'Context';
import { Page } from 'Components';
import { axios, baseUrl, dataFetcher, endpoints } from 'Api';
import { markSectionExplored, EXPLORED_SECTION } from 'Utils/onboardingChecklist';
import { getVideosDetail } from 'Api/apiFunctions';
import { logAppEvent } from 'Api/errorLogger';
import RotatingLoader from 'Components/RotatingLoader';
import AddPlaylistModal from 'Components/AddPlaylistModal';
import AddAlbumModal from 'Components/AddAlbumModal';
import { ShareItemModal } from 'Components/share-item-modal';
import { useBulkDownloadAll, DOWNLOAD_MODES } from 'hooks/useBulkDownloadAll';
import { ReactComponent as DragIcon } from 'Assets/drag.svg';
import AlterImage from 'Assets/alter-image.png';
import {
  Box, CircularProgress, Dialog, Zoom, FormControl, Select, MenuItem, Menu, ListItemIcon, Tooltip,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  VideoLibrary as AllVideosIcon,
  PhotoLibrary as PhotoIcon,
  PlaylistPlay as PlaylistIcon,
  Add as AddIcon,
  ArrowBack as BackIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteOutlineIcon,
  Sort as SortIcon,
  Event as DateIcon,
  TextFields as TextIcon,
  ViewList as LayoutIcon,
  ArrowUpward,
  ArrowDownward,
  Cancel as CancelIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { X as CloseX, Check as CheckIcon, ArrowDownUp } from 'lucide-react';
import {
  AnimatedEdit, AnimatedDownload, AnimatedShare, AnimatedTrash,
  AnimatedPrivate, AnimatedGroup, AnimatedPublic, AnimatedFilePenLine,
} from 'Components/reactbits/AnimatedIcons';
import SpotlightCard from 'Components/reactbits/SpotlightCard';
import { VideoEditModal } from 'Components/VideoUploadModal';
import PhotoEditModal from 'Components/PhotoUploadModal/PhotoEditModal';
import CategoryEditModal from '../v2/content-layout/CategoryEditModal';
import SaveIndicator from '../v2/content-layout/SaveIndicator';
import LivePreviewPop from '../v2/content-layout/LivePreviewPop';
import CreateCategoryPage from '../content-layout/CreateCategory';
import photoDeleteStyles from '../videos/components/AlbumPhotosView.module.css';
import { V2ThemeProvider } from '../v2/_shared/V2Layout';
import styles from './index.module.css';

// Human-readable helpers for the card hover panel (best-effort across field shapes).
const formatBytes = (n) => {
  if (n == null || n === '') return '';
  if (typeof n === 'string' && /[a-zA-Z]/.test(n)) return n; // already formatted
  const num = typeof n === 'string' ? parseFloat(n) : n;
  if (!num || Number.isNaN(num)) return '';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = num;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};
// Filesystem-safe file name, same rules /your-videos uses for downloads.
const sanitizeFileName = (name) => {
  if (!name) return 'untitled';
  const sanitized = String(name)
    .normalize('NFKC')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F<>:"/\\|?*\u2028\u2029]+/g, '') // control + reserved chars
    .replace(/\s+/g, '_')
    .replace(/_{2,}/g, '_')
    .trim();
  return sanitized || 'untitled';
};
const formatDate = (d) => {
  if (!d) return '';
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return '';
  return t.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

// Visibility code → label + animated icon (matches VideoEditModal: 1 Private,
// 2 All Viewers, 3 Group). hat-glasses = private, users = group, waypoints = public.
const visibilityMeta = (v) => {
  if (v == null || v === '') return null;
  const s = String(v);
  if (s === '1') return { label: 'Private', Icon: AnimatedPrivate, color: '#EF4444' };
  if (s === '3') return { label: 'Group', Icon: AnimatedGroup, color: '#8B5CF6' };
  if (s === '2') return { label: 'All Viewers', Icon: AnimatedPublic, color: '#10B981' };
  return null;
};

// Skeleton Card Component
const SkeletonCard = () => (
  <div className={styles.skeletonCard}>
    <div className={styles.skeletonThumbnail} />
    <div className={styles.skeletonInfo}>
      <div className={styles.skeletonTitle} />
      <div className={styles.skeletonMeta} />
    </div>
  </div>
);

// Sidebar Skeleton Component
const SidebarSkeleton = () => (
  <div className={styles.sidebarSkeleton}>
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className={styles.skeletonMenuItem}>
        <div className={styles.skeletonIcon} />
        <div className={styles.skeletonText} />
      </div>
    ))}
  </div>
);

// Content Item Card — reactbits SpotlightCard shell with a hover-reveal info panel.
const ContentItemCard = React.memo(({
  item,
  cardId,
  groupType,
  isDragging,
  isOverlay,
  isAllVideos,
  isSelected,
  onToggleSelect,
  viewMode = 'grid',
  isHighlighted,
  onEdit,
  onDelete,
  onDownload,
  canDelete = true,
}) => {
  const thumbnail = groupType === '3'
    ? (item.imagekit_url || item.image || AlterImage)
    : (item.imagekit_icon || item.thumbnails?.[0] || item.thumbnail || AlterImage);

  const title = groupType === '3'
    ? (item.title || item.image?.split('/').pop() || '')
    : (item.title || 'Untitled');

  const vis = visibilityMeta(item.visibility ?? item.privacy);
  const size = formatBytes(item.size ?? item.file_size ?? item.fileSize ?? item.video_size);
  const dateStr = formatDate(item.created_at || item.published_at || item.createdAt || item.uploaded_at || item.date);

  const stopDrag = (e) => e.stopPropagation();

  return (
    <SpotlightCard
      data-cardid={cardId ?? (item.id || item.content_id || item.video_id || item.photo_id)}
      /* Spotlight disabled (was a dark navy glow) — the hover treatment is the
         bento gradient border instead. */
      spotlightColor="rgba(0, 0, 0, 0)"
      className={cn({
        [styles.contentCard]: true,
        [styles.listCard]: viewMode === 'list',
        [styles.dragging]: isDragging,
        [styles.overlay]: isOverlay,
        [styles.noDrag]: isAllVideos,
        [photoDeleteStyles.selectedCard]: isSelected,
        [styles.highlighted]: isHighlighted,
      })}
    >
      <div className={styles.cardThumbnail}>
        <img
          src={thumbnail}
          alt={title}
          loading="lazy"
          decoding="async"
          // Reveal the tile only once its image is ready (covers cached images
          // via the ref's `complete` check, plus the error fallback).
          ref={(el) => { if (el && el.complete) el.setAttribute('data-loaded', 'true'); }}
          onLoad={(e) => { e.currentTarget.dataset.loaded = 'true'; }}
          onError={(e) => { e.currentTarget.src = AlterImage; e.currentTarget.dataset.loaded = 'true'; }}
        />
        {!isOverlay && onToggleSelect && (
          <div
            role="button"
            tabIndex={0}
            aria-label="Select"
            aria-pressed={isSelected}
            className={cn(styles.tileCheckWrap, { [styles.tileCheckWrapOn]: isSelected })}
            onPointerDown={stopDrag}
            onMouseDown={stopDrag}
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect(item);
            }}
          >
            <span className={cn(styles.tileCheck, { [styles.tileCheckOn]: isSelected })}>
              {isSelected && <CheckIcon size={13} strokeWidth={3} />}
            </span>
          </div>
        )}
        {!isAllVideos && (
          <div className={styles.dragHandle}>
            <DragIcon />
          </div>
        )}
      </div>

      {!isOverlay && (
        <div className={styles.cardHover}>
          <div className={styles.cardHoverTop}>
            <span className={styles.cardHoverTitle} title={title}>{title}</span>
            {vis && (
              <span className={styles.cardHoverVis}>
                <span className={styles.cardHoverVisIcon} style={{ color: vis.color }}>
                  <vis.Icon size={13} strokeWidth={2.2} />
                </span>
                {vis.label}
              </span>
            )}
          </div>
          {(size || dateStr) && (
            <div className={styles.cardHoverMeta}>{[size, dateStr].filter(Boolean).join('  ·  ')}</div>
          )}
          <div className={styles.cardHoverActions}>
            {canDelete && onDelete && (
              <button
                type="button"
                className={styles.cardActBtn}
                title="Delete"
                aria-label="Delete"
                onPointerDown={stopDrag}
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
              >
                <AnimatedTrash size={15} strokeWidth={2.2} />
              </button>
            )}
            {onEdit && (
              <button
                type="button"
                className={styles.cardActBtn}
                title="Edit"
                aria-label="Edit"
                onPointerDown={stopDrag}
                onClick={(e) => { e.stopPropagation(); onEdit(); }}
              >
                <AnimatedEdit size={15} strokeWidth={2.2} />
              </button>
            )}
            {onDownload && (
              <button
                type="button"
                className={styles.cardActBtn}
                title="Download"
                aria-label="Download"
                onPointerDown={stopDrag}
                onClick={(e) => { e.stopPropagation(); onDownload(); }}
              >
                <AnimatedDownload size={15} strokeWidth={2.2} />
              </button>
            )}
          </div>
        </div>
      )}
    </SpotlightCard>
  );
});

// Sortable Item wrapper using @dnd-kit
const SortableItem = ({
  id, item, groupType, isAllVideos, isSelected, onToggleSelect, viewMode = 'grid', isHighlighted,
  onEdit, onDelete, onDownload, canDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: isAllVideos });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isAllVideos ? 'not-allowed' : 'grab',
    minWidth: 0, // Prevent item from expanding beyond grid cell
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      title={isAllVideos ? "This can't be reordered" : undefined}
      {...(isAllVideos ? {} : { ...attributes, ...listeners })}
    >
      <ContentItemCard
        item={item}
        cardId={id}
        groupType={groupType}
        isDragging={isDragging}
        isAllVideos={isAllVideos}
        isSelected={isSelected}
        onToggleSelect={onToggleSelect}
        viewMode={viewMode}
        isHighlighted={isHighlighted}
        onEdit={onEdit}
        onDelete={onDelete}
        onDownload={onDownload}
        canDelete={canDelete}
      />
    </div>
  );
};

// Small helper to keep the opacity ternary readable.
const sidebarItemOpacity = (dragging, isDisabled) => {
  if (dragging) return 0.5;
  if (isDisabled) return 0.6;
  return 1;
};

// Sidebar item as a framer-motion Reorder.Item. dragListener is disabled so a drag
// only starts from the drag handle — the body stays a click target for selection.
const SortableSidebarItem = ({
  item,
  isSelected,
  isPinned,
  onItemClick,
  onEdit,
  showEdit,
  getTypeIcon,
  groupType,
  isDisabled,
  onDragStart,
  onDragSettle,
  stackedThumbs,
}) => {
  const controls = useDragControls();
  const [dragging, setDragging] = React.useState(false);

  // 100x66 thumbnail for categories and albums; playlists have no image of
  // their own from the backend, so fall back to a fanned stack built from
  // up to 3 of the playlist's own video thumbnails (see fetchPlaylistStackThumbs).
  const thumbImage = (groupType === '1' || groupType === '3')
    ? (item.imagekit_icon || item.icon)
    : null;

  const count = groupType === '3'
    ? (item.photo_count ?? item.photos_count ?? item.count)
    : (item.video_count ?? item.videos_count ?? item.count);
  const countLabel = count != null ? `${count} ${groupType === '3' ? 'photos' : 'videos'}` : null;

  return (
    <Reorder.Item
      as="div"
      value={item}
      dragListener={false}
      dragControls={controls}
      onDragStart={() => { setDragging(true); onDragStart(item.id); }}
      onDragEnd={() => { setDragging(false); onDragSettle(); }}
      whileDrag={{ scale: 1.02, zIndex: 5 }}
      transition={{ type: 'spring', stiffness: 500, damping: 40 }}
      style={{
        opacity: sidebarItemOpacity(dragging, isDisabled),
        pointerEvents: isDisabled ? 'none' : 'auto',
      }}
      className={cn(styles.menuItem, {
        [styles.active]: isSelected,
        [styles.draggingSidebar]: dragging,
        [styles.disabled]: isDisabled,
      })}
      onClick={() => onItemClick(item)}
      role="button"
      tabIndex={0}
    >
      <div
        className={styles.sidebarDragHandle}
        onPointerDown={(e) => { if (!isDisabled) controls.start(e); }}
        style={{ cursor: isDisabled ? 'default' : 'grab', touchAction: 'none' }}
      >
        <DragIcon />
      </div>
      {thumbImage ? (
        <span className={styles.menuThumb} style={{ backgroundImage: `url(${thumbImage})` }} />
      ) : stackedThumbs?.length > 0 ? (
        <span className={styles.menuThumbStack}>
          {stackedThumbs.slice(0, 3).map((url, i) => (
            <span
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              className={styles.menuThumbStackItem}
              style={{ backgroundImage: `url(${url})` }}
            />
          ))}
        </span>
      ) : (
        <span className={cn(styles.menuThumb, styles.menuThumbIcon)}>
          {isPinned ? <AllVideosIcon /> : getTypeIcon(isSelected)}
        </span>
      )}
      <div className={styles.menuContent}>
        <span className={styles.menuLabel}>{item.title}</span>
        {countLabel && <span className={styles.menuSub}>{countLabel}</span>}
      </div>
      {showEdit && !isPinned && (
        <button
          type="button"
          className={styles.sidebarEditBtn}
          onClick={(e) => { e.stopPropagation(); onEdit(item); }}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label={`Edit ${item.title}`}
          title="Edit"
        >
          <AnimatedFilePenLine size={16} />
        </button>
      )}
    </Reorder.Item>
  );
};

// One action in the selection pill; drives its lucide icon's animation on hover.
const PillAction = ({ Icon, label, onClick, danger }) => {
  const [hovered, setHovered] = React.useState(false);
  return (
    <button
      type="button"
      className={cn(styles.selBtn, { [styles.selDanger]: danger })}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={label}
      aria-label={label}
    >
      <Icon size={18} strokeWidth={2.2} animate={hovered} />
      <span>{label}</span>
    </button>
  );
};

// Check-square glyph for the pill's "Select all" action. Accepts the same props
// PillAction passes (size/strokeWidth/animate); `animate` is intentionally
// ignored so no unknown-prop warning reaches the DOM.
const SelectAllIcon = ({ size = 18, strokeWidth = 2.2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 11l3 3L22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
);

export const ContentLayoutDetails = ({
  groupTypeProp, hideBack = false, topOffset = 0, showHeader = false, headerLeading = null,
  disableReorder = false, sidebarEditable = false, showDownloadAll = false,
} = {}) => {
  const {
    setAppSnackbar, langText, defaultCategoryName, userDetails,
    // Download plumbing — the same globals /your-videos drives, so a card
    // download shows up in the shared download panel (progress/cancel).
    setDownloadingFiles, setDownloadBox, downloadAbortControllersRef, bulkDownloadCancelRef,
  } = useContext(AppContext);
  const { getToken } = useContext(AuthContext);
  // "Download all" — same control (and hook) /your-videos uses at the top of
  // its page. Aliased so its `preparingDownload` doesn't collide with the
  // single-album download busy flag declared just below.
  const {
    downloadAllContent,
    preparingDownload: preparingBulkDownload,
    bulkDownloadProgress,
    downloadMenuAnchor: bulkDownloadMenuAnchor,
    setDownloadMenuAnchor: setBulkDownloadMenuAnchor,
  } = useBulkDownloadAll();
  const bulkDownloadControlRef = React.useRef(null);
  // Album download: folder the user picked, plus a busy flag while a run sets up.
  const directoryHandleRef = React.useRef(null);
  const [preparingDownload, setPreparingDownload] = useState(false);
  const navigate = useNavigate();
  const { layoutType } = useParams();
  const commonText = langText?.common;
  const layoutText = langText?.layout;

  // Driven by the route param on /content-layout/:layoutType, or by a prop when
  // embedded (e.g. Your Videos, where a PillNav picks the type).
  const groupType = groupTypeProp || layoutType; // '1' = Categories, '2' = Playlists, '3' = Albums

  // Sidebar data (list of categories/playlists/albums)
  const [sidebarItems, setSidebarItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [sidebarLoading, setSidebarLoading] = useState(true);

  // Playlists have no thumbnail of their own from the backend — for each
  // playlist row (with videos) we lazily fetch up to 3 of its videos and use
  // their thumbnails as a fanned "stacked" preview. { [playlistId]: [url, ...] }
  const [playlistStackThumbs, setPlaylistStackThumbs] = useState({});
  const playlistThumbsFetchedRef = React.useRef(new Set());

  // Content data (videos/photos in selected item)
  const [contentItems, setContentItems] = useState([]);
  const [contentLoading, setContentLoading] = useState(false);

  // Grid vs list view toggle for the right-side content area
  const [viewMode, setViewMode] = useState('grid');

  // Grid search — scroll-to + transient highlight of the matching tile.
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightId, setHighlightId] = useState(null);
  const highlightTimerRef = React.useRef(null);

  // Sequential queue processing with reorderItem API
  const [isSaving, setIsSaving] = useState(false);
  const [isSidebarSaving, setIsSidebarSaving] = useState(false);
  const [isDraggingContent, setIsDraggingContent] = useState(false);
  const [isDraggingSidebar, setIsDraggingSidebar] = useState(false);
  const [queueCount, setQueueCount] = useState(0);
  const [sidebarQueueCount, setSidebarQueueCount] = useState(0);
  const reorderQueueRef = React.useRef([]);
  const sidebarReorderQueueRef = React.useRef([]);
  const isProcessingQueueRef = React.useRef(false);
  const isSidebarProcessingQueueRef = React.useRef(false);
  const reorderTimerRef = React.useRef(null);
  const sidebarReorderTimerRef = React.useRef(null);
  const DEBOUNCE_DELAY = 200; // 200ms delay - just enough to ensure user finished dragging
  // framer-motion sidebar drag: onReorder fires live, so the drag-end handler reads
  // the settled order from a ref plus the dragged id / start index captured at start.
  const sidebarItemsRef = React.useRef([]);
  const draggingSidebarIdRef = React.useRef(null);
  const draggingSidebarStartIndexRef = React.useRef(-1);

  // @dnd-kit: Active drag item for the content-grid overlay
  const [activeId, setActiveId] = useState(null);

  // @dnd-kit: Configure sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Start drag after 8px movement
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Infinite scroll state
  const [totalItems, setTotalItems] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Smaller first page = faster first paint; the rest lazy-loads on scroll.
  const itemsPerPage = 24;
  const contentContainerRef = React.useRef(null);
  // Bottom-of-list marker watched by the IntersectionObserver below.
  const loadMoreSentinelRef = React.useRef(null);
  // Synchronous load-more guards — scroll fires many times per second, well
  // before the loadingMore state re-render lands, so a state check alone lets
  // duplicate pages slip through. Refs flip immediately.
  const isLoadingMoreRef = React.useRef(false);
  const isSidebarLoadingMoreRef = React.useRef(false);
  // Set once we've PROVEN the content endpoint ignores `start` (see the retry in
  // fetchContentForItem). Reset whenever a new item is opened.
  const startIsIgnoredRef = React.useRef(false);

  // Sort state (mirrors /innerpage SortControls). 'layout' is the default
  // ordering returned by the API — no sort params sent in that case.
  const [sortBy, setSortBy] = useState('layout');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [isSorting, setIsSorting] = useState(false);
  const [quickSortAnchor, setQuickSortAnchor] = useState(null);

  // Quick reorder: persists the currently-applied sort as the saved layout
  // order via applyContentSort. Only meaningful when sortBy !== 'layout'.
  const [showApplySortModal, setShowApplySortModal] = useState(false);
  const [isApplyingSort, setIsApplyingSort] = useState(false);

  // Revert window: after a successful applyContentSort the backend snapshots
  // the previous order, so we swap the Quick Reorder button for a one-shot
  // "Revert" button (with a 30s countdown + manual close) tied to the
  // container that was just sorted. The snapshot is consumed on revert, so the
  // button hides after success / countdown / a second sort / item switch.
  const REVERT_WINDOW_SECONDS = 30;
  const [revertVisible, setRevertVisible] = useState(false);
  const [revertCountdown, setRevertCountdown] = useState(REVERT_WINDOW_SECONDS);
  const [isReverting, setIsReverting] = useState(false);
  const revertParentIdRef = React.useRef(null);
  const revertTimerRef = React.useRef(null);

  const clearRevertTimer = () => {
    if (revertTimerRef.current) {
      clearInterval(revertTimerRef.current);
      revertTimerRef.current = null;
    }
  };

  const hideRevert = () => {
    clearRevertTimer();
    setRevertVisible(false);
    revertParentIdRef.current = null;
  };

  // Start (or restart) the 30s revert window for the just-sorted container.
  const startRevertCountdown = (parentId) => {
    clearRevertTimer();
    revertParentIdRef.current = parentId;
    setRevertCountdown(REVERT_WINDOW_SECONDS);
    setRevertVisible(true);
    revertTimerRef.current = setInterval(() => {
      setRevertCountdown((prev) => {
        if (prev <= 1) {
          clearRevertTimer();
          setRevertVisible(false);
          revertParentIdRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Clear the timer if the component unmounts mid-countdown.
  useEffect(() => () => clearRevertTimer(), []);

  // Sidebar pagination state — load 20 at a time, append on scroll near
  // the bottom of the .menuList container, render a single shimmer row
  // below the last item while the next page is in flight.
  // Load the whole sidebar in one light (thumbnails:false) request instead of
  // paginating on scroll — the scroll-load was janky, this is crisp.
  const SIDEBAR_PER_PAGE = 200;
  const [sidebarHasMore, setSidebarHasMore] = useState(true);
  const [sidebarLoadingMore, setSidebarLoadingMore] = useState(false);
  const sidebarListRef = React.useRef(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  // Sub-child edit → the new in-place CategoryEditModal (name + image + reorder).
  const [layoutEditItem, setLayoutEditItem] = useState(null);

  // Multi-select + delete state — drives the selection panel and confirm
  // dialog for both album photos (groupType '3') and category videos
  // (groupType '1'). The UX mirrors the photo-album flow so both look
  // identical; only the delete endpoint differs (deleteVideo for category
  // videos, matching the /your-videos videos-tab bulk delete API).
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photosToDelete, setPhotosToDelete] = useState([]);
  const [isDeletingPhoto, setIsDeletingPhoto] = useState(false);
  const [photosPanelTransitionState, setPhotosPanelTransitionState] = useState('exited');
  const [editItemTarget, setEditItemTarget] = useState(null);
  // Share-link state (create/revoke a public /share/:key link for one item)
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareRevoking, setShareRevoking] = useState(false);
  const [shareTarget, setShareTarget] = useState(null); // { type, id }

  // Multi-select is enabled for photo albums and category videos. Playlists
  // (groupType '2') still use the row-level remove button.
  const isMultiSelectEnabled = groupType === '3' || groupType === '1';
  const isVideoSelection = groupType === '1';
  const selectionLabel = isVideoSelection ? 'Video' : 'Photo';
  const selectionLabelPlural = isVideoSelection ? 'Videos' : 'Photos';

  const isPhotoSelected = (photoId) => selectedPhotos.some((p) => p?.id === photoId);
  const togglePhotoSelection = (photo) => {
    setSelectedPhotos((prev) => {
      const exists = prev.some((p) => p?.id === photo?.id);
      if (exists) return prev.filter((p) => p?.id !== photo?.id);
      return [...prev, photo];
    });
  };

  // Shared by the video and album flows below.
  const triggerAnchorDownload = (href, filename) => {
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Videos download the same way /your-videos does: resolve the ORIGINAL file
  // through getVideosDetail (video_raw_file) and stream it into the shared
  // download panel. The card was guessing playback URLs (imagekit_video /
  // video_url), which the category/playlist responses don't carry — so clicking
  // a card's download did nothing.
  const downloadVideoItems = async (items) => {
    if (bulkDownloadCancelRef) bulkDownloadCancelRef.current = false;
    setDownloadBox(true);

    const batchStamp = Date.now();
    const entries = items.map((it, idx) => {
      const videoId = it.id || it.content_id || it.video_id;
      const name = it.title || `video_${videoId}`;
      return {
        id: `${videoId}_${batchStamp}_${idx}`,
        videoId,
        name,
        fileName: `${sanitizeFileName(name)}.${it.extension || 'mp4'}`,
        extension: it.extension,
        progress: 0,
        status: 'pending',
        source: 'bulk',
        type: 'video',
      };
    });
    setDownloadingFiles((prev) => [...prev, ...entries]);

    let completed = 0;
    let cancelled = false;

    // eslint-disable-next-line no-restricted-syntax
    for (const entry of entries) {
      if (bulkDownloadCancelRef?.current) { cancelled = true; break; }

      setDownloadingFiles((prev) => prev.map((f) => (f.id === entry.id
        ? { ...f, status: 'downloading' }
        : f)));

      const controller = new AbortController();
      downloadAbortControllersRef?.current?.set(entry.id, controller);

      try {
        // eslint-disable-next-line no-await-in-loop
        const detail = await getVideosDetail({ id: entry.videoId });
        const rawFile = detail?.data?.success ? detail?.data?.data?.video_raw_file : null;
        if (!rawFile || !rawFile.trim()) throw new Error('video_raw_file URL is missing or empty');

        // eslint-disable-next-line no-await-in-loop
        const response = await fetch(rawFile, { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const total = parseInt(response.headers.get('content-length') || '0', 10);
        const reader = response.body.getReader();
        const chunks = [];
        let received = 0;
        let lastReceived = 0;
        let lastStamp = Date.now();
        let speed = 0;

        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (bulkDownloadCancelRef?.current || controller.signal.aborted) {
            // eslint-disable-next-line no-await-in-loop
            await reader.cancel();
            cancelled = true;
            break;
          }

          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();
          if (done) break;

          chunks.push(value);
          received += value.length;

          const now = Date.now();
          const elapsed = (now - lastStamp) / 1000;
          if (elapsed >= 0.5) {
            speed = (received - lastReceived) / elapsed;
            lastReceived = received;
            lastStamp = now;
          }
          const remaining = total > 0 ? total - received : 0;
          const eta = (speed > 0 && remaining > 0) ? Math.round(remaining / speed) : 0;
          const progress = total > 0 ? Math.round((received / total) * 100) : 0;
          const speedSnapshot = speed;
          const receivedSnapshot = received;
          setDownloadingFiles((prev) => prev.map((f) => (f.id === entry.id
            ? {
              ...f,
              progress,
              status: 'downloading',
              speed: speedSnapshot,
              eta,
              totalSize: total,
              downloadedSize: receivedSnapshot,
            }
            : f)));
        }

        if (cancelled) break;

        const ext = entry.extension
          || (rawFile.split('?')[0].split('.').pop() || 'mp4').toLowerCase();
        const mimeTypes = {
          mp4: 'video/mp4', mov: 'video/quicktime', avi: 'video/x-msvideo', webm: 'video/webm', mkv: 'video/x-matroska',
        };
        const objUrl = URL.createObjectURL(new Blob(chunks, { type: mimeTypes[ext] || 'video/mp4' }));
        triggerAnchorDownload(objUrl, `${sanitizeFileName(entry.name)}.${ext}`);
        setTimeout(() => URL.revokeObjectURL(objUrl), 10000);

        setDownloadingFiles((prev) => prev.map((f) => (f.id === entry.id
          ? { ...f, progress: 100, status: 'completed' }
          : f)));
        completed += 1;

        // Small gap so the browser registers each download when several run
        if (entries.length > 1) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      } catch (err) {
        if (err?.name === 'AbortError' || bulkDownloadCancelRef?.current) {
          cancelled = true;
          break;
        }
        setDownloadingFiles((prev) => prev.map((f) => (f.id === entry.id
          ? { ...f, status: 'error' }
          : f)));
        logAppEvent({
          userName: `${userDetails?.firstname || ''} ${userDetails?.lastname || ''}`.trim() || 'Guest',
          functionAffected: 'Download',
          description: `Single video download failed for "${entry.name}" (ID: ${entry.videoId})`,
          severity: 'high',
          errorMessage: err?.message || 'Single video download failed',
          requestUrl: endpoints.getVideosDetail,
          requestMethod: 'POST',
          component: 'ContentLayoutDetails',
          userAction: 'Download video called',
        });
      } finally {
        downloadAbortControllersRef?.current?.delete(entry.id);
      }
    }

    setAppSnackbar({
      isVisible: true,
      type: completed > 0 ? 'success' : 'error',
      message: cancelled
        ? `Download canceled. ${completed} ${completed === 1 ? 'item' : 'items'} downloaded`
        : completed > 0
          ? `Download completed: ${completed} video${completed !== 1 ? 's' : ''}`
          : 'Download failed',
    });

    // Drop this batch from the panel once the user has seen the result
    setTimeout(() => {
      setDownloadingFiles((prev) => {
        const remaining = prev.filter((f) => !entries.some((e) => e.id === f.id));
        if (remaining.length === 0) setDownloadBox(false);
        return remaining;
      });
    }, 2000);
  };

  /**
   * Download whole albums — /your-videos' downloadSelectedAlbums, unchanged
   * (same code in the displayNameFix branch). Asks for a folder, then writes each
   * album's photos into a folder named after the album; iOS/Safari falls back to
   * sequential downloads prefixed with the album name.
   */
  const downloadAlbums = async (albumsToDownload) => {
    if (!albumsToDownload || albumsToDownload.length === 0) {
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: 'No albums selected for download',
      });
      return;
    }

    // Use already-loaded photos when available, otherwise fetch album detail
    const getAlbumPhotos = async (album) => {
      if (Array.isArray(album.photos) && album.photos.length > 0) return album.photos;
      try {
        const albumDetail = await axios.post(`${baseUrl}/getMyAlbumDetail`, {
          album_id: album.content_id,
          token: getToken(),
        });
        return albumDetail?.data?.data?.[0]?.photos || [];
      } catch (error) {
        return [];
      }
    };

    // iOS/Safari fallback: File System Access API not supported
    if (typeof window.showDirectoryPicker !== 'function') {
      if (bulkDownloadCancelRef) {
        bulkDownloadCancelRef.current = false;
      }
      setPreparingDownload(true);
      setDownloadBox(true);

      const batchTimestamp = Date.now();
      const trackingEntries = albumsToDownload.map((album, idx) => ({
        id: `album_${album.content_id}_${batchTimestamp}_${idx}`,
        albumId: album.content_id,
        name: album.title || `album_${album.content_id}`,
        fileName: album.title || `album_${album.content_id}`,
        progress: 0,
        status: 'pending',
        source: 'bulk',
        totalPhotos: album.photos?.length || 0,
        type: 'photo',
      }));
      setDownloadingFiles((prev) => [...prev, ...trackingEntries]);
      setPreparingDownload(false);

      let completedCount = 0;
      let wasCancelled = false;

      for (let aIdx = 0; aIdx < albumsToDownload.length; aIdx += 1) {
        if (bulkDownloadCancelRef?.current) {
          wasCancelled = true;
          break;
        }

        const album = albumsToDownload[aIdx];
        const entry = trackingEntries[aIdx];
        const albumTitle = sanitizeFileName(album.title || `album_${album.content_id}`);

        setDownloadingFiles((prev) => prev.map((f) => (f.id === entry.id
          ? { ...f, status: 'downloading' }
          : f)));

        // eslint-disable-next-line no-await-in-loop
        const photos = await getAlbumPhotos(album);
        setDownloadingFiles((prev) => prev.map((f) => (f.id === entry.id
          ? { ...f, totalPhotos: photos.length }
          : f)));

        let completedPhotos = 0;

        // eslint-disable-next-line no-restricted-syntax
        for (const photo of photos) {
          if (bulkDownloadCancelRef?.current) {
            wasCancelled = true;
            break;
          }

          const photoUrl = photo.photo_file || photo.url || photo.image;
          if (!photoUrl) {
            completedPhotos += 1;
            // eslint-disable-next-line no-continue
            continue;
          }

          try {
            const controller = new AbortController();
            downloadAbortControllersRef.current.set(`${entry.id}_${completedPhotos}`, controller);

            // eslint-disable-next-line no-await-in-loop
            const photoResponse = await fetch(photoUrl, { signal: controller.signal });
            // eslint-disable-next-line no-await-in-loop
            const blob = await photoResponse.blob();

            downloadAbortControllersRef.current.delete(`${entry.id}_${completedPhotos}`);

            const ext = photoUrl.split('?')[0].split('.').pop() || 'jpg';
            const fileName = `${albumTitle}_photo_${completedPhotos + 1}.${ext}`;

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
              URL.revokeObjectURL(url);
              document.body.removeChild(a);
            }, 1000);

            completedPhotos += 1;
            completedCount += 1;

            const completedSnapshot = completedPhotos;
            const albumProgress = photos.length > 0
              ? Math.round((completedSnapshot / photos.length) * 100)
              : 100;
            setDownloadingFiles((prev) => prev.map((f) => (f.id === entry.id
              ? { ...f, progress: albumProgress, status: completedSnapshot === photos.length ? 'completed' : 'downloading' }
              : f)));

            // Small delay between downloads so iOS Safari can register each one
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, 1500));
          } catch (err) {
            if (err.name === 'AbortError' || bulkDownloadCancelRef?.current) {
              wasCancelled = true;
              break;
            }
            // eslint-disable-next-line no-console
            console.error(`Failed to download photo from album ${album.content_id}:`, err);
            completedPhotos += 1;
          }
        }

        setDownloadingFiles((prev) => prev.map((f) => (f.id === entry.id
          ? { ...f, progress: 100, status: completedPhotos >= photos.length ? 'completed' : 'error' }
          : f)));

        if (wasCancelled) break;
      }

      setAppSnackbar({
        isVisible: true,
        type: completedCount > 0 ? 'success' : 'error',
        message: wasCancelled
          ? `Download canceled. ${completedCount} ${completedCount === 1 ? 'photo' : 'photos'} downloaded`
          : completedCount > 0
            ? `Download completed: ${completedCount} ${completedCount === 1 ? 'photo' : 'photos'} downloaded`
            : 'Download failed',
      });

      setTimeout(() => {
        setDownloadingFiles((prev) => {
          const remaining = prev.filter((f) => !trackingEntries.some((e) => e.id === f.id));
          if (remaining.length === 0) setDownloadBox(false);
          return remaining;
        });
      }, 2000);

      return;
    }

    // Chrome / desktop: File System Access API
    // 1. Directory picker MUST run immediately when button is clicked
    let directoryHandle;
    try {
      directoryHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Directory Picker Error =>', err);
      const isUserAbort = err?.name === 'AbortError';
      logAppEvent({
        userName: `${userDetails?.firstname || ''} ${userDetails?.lastname || ''}`.trim() || 'Guest',
        functionAffected: 'Download',
        description: `Selected albums download cancelled — ${isUserAbort ? 'user dismissed the directory picker' : `directory picker failed: ${err?.name}`}`,
        severity: isUserAbort ? 'low' : 'high',
        errorMessage: err?.message || 'showDirectoryPicker failed',
        component: 'ContentLayoutDetails',
        userAction: 'Multiple selected albums download called',
      });
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Download canceled',
      });
      return;
    }

    directoryHandleRef.current = directoryHandle;

    // Close the selection pill once the download starts (folder picked)
    setSelectedPhotos([]);

    setPreparingDownload(true);
    let batchTrackingIds = new Set();

    if (bulkDownloadCancelRef) {
      bulkDownloadCancelRef.current = false;
    }

    try {
      setDownloadBox(true);

      const batchTimestamp = Date.now();
      const albumFiles = albumsToDownload.map((album, idx) => ({
        id: `album_${album.content_id}_${batchTimestamp}_${idx}`,
        albumId: album.content_id,
        name: album.title || `album_${album.content_id}`,
        fileName: album.title || `album_${album.content_id}`,
        progress: 0,
        status: 'pending',
        source: 'bulk',
        totalPhotos: album.photos?.length || 0,
        type: 'photo',
      }));
      setDownloadingFiles((prev) => [...prev, ...albumFiles]);
      batchTrackingIds = new Set(albumFiles.map((af) => af.id));
      setPreparingDownload(false);

      let completedCount = 0;

      for (let aIdx = 0; aIdx < albumsToDownload.length; aIdx += 1) {
        if (bulkDownloadCancelRef?.current) break;

        const album = albumsToDownload[aIdx];
        const entry = albumFiles[aIdx];
        const albumTitle = sanitizeFileName(album.title || `album_${album.content_id}`);

        setDownloadingFiles((prev) => prev.map((f) => (f.id === entry.id
          ? { ...f, status: 'downloading' }
          : f)));

        const albumController = new AbortController();
        downloadAbortControllersRef.current.set(entry.id, albumController);

        try {
          // eslint-disable-next-line no-await-in-loop
          const photos = await getAlbumPhotos(album);
          setDownloadingFiles((prev) => prev.map((f) => (f.id === entry.id
            ? { ...f, totalPhotos: photos.length }
            : f)));

          // Create a folder named after the album (unique if a folder already exists)
          let folderName = albumTitle;
          let copyNum = 1;
          // eslint-disable-next-line no-constant-condition
          while (true) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await directoryHandle.getDirectoryHandle(folderName);
              folderName = `${albumTitle} (${copyNum})`;
              copyNum += 1;
            } catch {
              break;
            }
          }
          // eslint-disable-next-line no-await-in-loop
          const albumFolderHandle = await directoryHandle.getDirectoryHandle(folderName, { create: true });

          const PHOTO_BATCH_SIZE = 2; // Download 2 photos at a time
          const completedPhotosCount = { value: 0 };

          for (let i = 0; i < photos.length; i += PHOTO_BATCH_SIZE) {
            if (bulkDownloadCancelRef?.current || albumController.signal.aborted) break;

            const batch = photos.slice(i, i + PHOTO_BATCH_SIZE);

            // eslint-disable-next-line no-await-in-loop
            await Promise.all(
              batch.map(async (photo, batchIndex) => {
                try {
                  if (bulkDownloadCancelRef?.current || albumController.signal.aborted) return;

                  const photoUrl = photo.photo_file || photo.url || photo.image;
                  if (!photoUrl) {
                    completedPhotosCount.value += 1;
                    return;
                  }

                  const photoIndex = i + batchIndex + 1;
                  const ext = photoUrl.split('?')[0].split('.').pop() || 'jpg';
                  const photoFileName = `photo_${photoIndex}.${ext}`;

                  const response = await fetch(photoUrl, { signal: albumController.signal });
                  const blob = await response.blob();

                  const fileHandle = await albumFolderHandle.getFileHandle(photoFileName, { create: true });
                  const writable = await fileHandle.createWritable();
                  await writable.write(blob);
                  await writable.close();

                  completedPhotosCount.value += 1;

                  const albumProgress = photos.length > 0
                    ? Math.round((completedPhotosCount.value / photos.length) * 100)
                    : 100;
                  setDownloadingFiles((prev) => prev.map((f) => (f.id === entry.id
                    ? { ...f, progress: albumProgress, status: completedPhotosCount.value === photos.length ? 'completed' : 'downloading' }
                    : f)));
                } catch (error) {
                  if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
                    return;
                  }
                  // eslint-disable-next-line no-console
                  console.error(`Failed to download photo from album ${album.content_id}:`, error);
                  completedPhotosCount.value += 1;
                }
              })
            );
          }

          downloadAbortControllersRef.current.delete(entry.id);
          completedCount += completedPhotosCount.value;

          setDownloadingFiles((prev) => prev.map((f) => (f.id === entry.id
            ? { ...f, status: 'completed', progress: 100 }
            : f)));
        } catch (error) {
          downloadAbortControllersRef.current.delete(entry.id);
          if (error.name === 'AbortError') {
            setDownloadingFiles((prev) => prev.map((f) => (f.id === entry.id
              ? { ...f, status: 'cancelled' }
              : f)));
          } else {
            // eslint-disable-next-line no-console
            console.error(`Failed to process album ${album.content_id}:`, error);
            logAppEvent({
              userName: `${userDetails?.firstname || ''} ${userDetails?.lastname || ''}`.trim() || 'Guest',
              functionAffected: 'Download',
              description: `Selected albums download failed — album download failed (ID: ${album.content_id})`,
              severity: 'high',
              errorMessage: error?.message || 'Album download failed',
              component: 'ContentLayoutDetails',
              userAction: 'Multiple selected albums download called',
            });
            setDownloadingFiles((prev) => prev.map((f) => (f.id === entry.id
              ? { ...f, status: 'error' }
              : f)));
          }
        }
      }

      setAppSnackbar({
        isVisible: true,
        type: 'success',
        message: bulkDownloadCancelRef?.current
          ? `Download canceled. ${completedCount} photos downloaded`
          : `Download completed: ${completedCount} photos downloaded successfully`,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Selected Albums Download Error:', err);
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Error during download',
      });
    } finally {
      setPreparingDownload(false);
      setDownloadingFiles((prev) => {
        const remaining = prev.filter((f) => !batchTrackingIds.has(f.id));
        if (remaining.length === 0) {
          setDownloadBox(false);
        }
        return remaining;
      });
    }
  };

  // The album this view is showing (sidebar selection) — every album-level
  // download here targets it.
  const downloadCurrentAlbum = () => {
    const album = selectedItem;
    if (!album) {
      setAppSnackbar({ isVisible: true, type: 'error', message: 'No album selected for download' });
      return;
    }
    downloadAlbums([{
      content_id: album.content_id ?? album.id,
      title: album.title,
    }]);
  };
  const downloadItems = (items) => {
    if (!items || !items.length) return;
    // Albums: a download here means "download this album" — the folder picker
    // flow from /your-videos. Videos go through the raw-file flow above.
    if (groupType === '3') {
      downloadCurrentAlbum();
      return;
    }
    downloadVideoItems(items);
  };
  const downloadSelected = () => downloadItems(selectedPhotos);

  // Resolve the share type + id for a single item. Videos mirror the delete
  // flow's draft_id/content_id resolution above (published videos whose
  // draft_id starts with 'old_video' share by content_id); albums use
  // content_id.
  const getShareTarget = (item) => {
    if (!item) return null;
    if (groupType === '3') {
      const id = item.content_id ?? item.id;
      return id != null ? { type: 'album', id } : null;
    }
    const isOldVideo = typeof item?.draft_id === 'string' && item.draft_id.startsWith('old_video');
    const id = (isOldVideo ? item.content_id : item.draft_id) ?? item.content_id ?? item.id;
    return id != null ? { type: 'video', id } : null;
  };

  const handleCreateShare = async (target) => {
    setShareTarget(target);
    setShareUrl('');
    setShareLoading(true);
    setShareModalOpen(true);
    try {
      const response = await axios.post(`${baseUrl}/createShare`, {
        type: target.type,
        id: target.id,
        token: getToken(),
      });
      if (response.data?.success && response.data?.data?.share_key) {
        setShareUrl(`${window.location.origin}/share/${response.data.data.share_key}`);
      } else {
        setShareModalOpen(false);
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: response.data?.message || 'Could not create the share link.',
        });
      }
    } catch (error) {
      setShareModalOpen(false);
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: error?.response?.data?.message || 'Could not create the share link.',
      });
    } finally {
      setShareLoading(false);
    }
  };

  const handleRevokeShare = async () => {
    if (!shareTarget) return;
    setShareRevoking(true);
    try {
      const response = await axios.post(`${baseUrl}/unshareItem`, {
        type: shareTarget.type,
        id: shareTarget.id,
        token: getToken(),
      });
      setShareModalOpen(false);
      setAppSnackbar({
        isVisible: true,
        type: response.data?.success ? 'success' : 'error',
        message: response.data?.message || 'Share link removed.',
      });
    } catch (error) {
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: error?.response?.data?.message || 'Could not remove the share link.',
      });
    } finally {
      setShareRevoking(false);
    }
  };

  // Share — a single selected item gets a real public /share/:key link
  // (createShare + the ShareItemModal below). Multiple items fall back to
  // copying each item's direct link, since createShare only takes one item
  // at a time.
  const shareItems = async (items) => {
    if (!items || !items.length) return;
    if (items.length === 1) {
      const target = getShareTarget(items[0]);
      if (target) {
        handleCreateShare(target);
        return;
      }
    }
    const links = items
      .map((it) => (groupType === '3'
        ? (it.imagekit_url || it.image || '')
        : `${window.location.origin}/video/${it.id}`))
      .filter(Boolean);
    try {
      await navigator.clipboard.writeText(links.join('\n'));
      setAppSnackbar({ isVisible: true, message: links.length > 1 ? `${links.length} links copied` : 'Link copied' });
    } catch (e) {
      setAppSnackbar({ isVisible: true, type: 'error', message: 'Could not copy link' });
    }
  };
  const shareSelected = () => shareItems(selectedPhotos);

  // Per-card / bulk delete both funnel through the same confirm dialog.
  const deleteItems = (items) => {
    if (!items || !items.length) return;
    setPhotosToDelete(items);
  };
  const requestBulkDelete = () => deleteItems(selectedPhotos);

  const handleCancelPhotoDelete = () => {
    if (isDeletingPhoto) return;
    setPhotosToDelete([]);
  };

  const deleteCount = photosToDelete.length;
  const firstDeleteCandidate = photosToDelete[0];

  const handleConfirmPhotoDelete = async () => {
    if (photosToDelete.length === 0) return;
    setIsDeletingPhoto(true);
    try {
      let response;
      let removedItemIds;
      if (isVideoSelection) {
        // Match /your-videos videos-tab bulk delete: deleteDraftContent
        // expects two arrays — draft_id for in-progress drafts and content_id
        // for already-published videos (whose draft_id starts with 'old_video').
        const draftIds = [];
        const contentIds = [];
        photosToDelete.forEach((item) => {
          const isOldVideo = typeof item?.draft_id === 'string'
            && item.draft_id.startsWith('old_video');
          if (isOldVideo && item?.content_id) {
            contentIds.push(item.content_id);
          } else if (item?.draft_id) {
            draftIds.push(item.draft_id);
          } else if (item?.content_id) {
            contentIds.push(item.content_id);
          }
        });
        if (draftIds.length === 0 && contentIds.length === 0) return;
        response = await dataFetcher(endpoints.deleteDraftContent, {
          draft_id: draftIds,
          content_id: contentIds,
        });
        removedItemIds = photosToDelete.map((p) => p?.id).filter(Boolean);
      } else {
        const ids = photosToDelete.map((p) => p?.id).filter(Boolean);
        if (ids.length === 0) return;
        response = await dataFetcher(endpoints.deleteMyPhoto, { photo_ids: ids });
        removedItemIds = ids;
      }
      // deleteDraftContent signals success via `message === true`; deleteMyPhoto
      // returns the standard `success: true` flag. Accept either shape.
      const succeeded = response?.success === true || response?.message === true;
      if (succeeded) {
        const idSet = new Set(removedItemIds);
        setContentItems((prev) => prev.filter((it) => !idSet.has(it.id)));
        setTotalItems((prev) => Math.max(0, prev - removedItemIds.length));
        setSelectedPhotos((prev) => prev.filter((p) => !idSet.has(p?.id)));
        setAppSnackbar({
          isVisible: true,
          message: removedItemIds.length === 1
            ? `${selectionLabel} Removed`
            : `${removedItemIds.length} ${selectionLabelPlural} Removed`,
        });
        setPhotosToDelete([]);
      } else {
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: response?.message && response.message !== true
            ? response.message
            : `Oops! Something went wrong while removing ${selectionLabel.toLowerCase()}`,
        });
      }
    } catch (err) {
      logAppEvent({
        userName: `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
        functionAffected: isVideoSelection ? 'Delete Category Video' : 'Delete Album Photo',
        description: isVideoSelection ? 'Category video delete failed' : 'Album photo delete failed',
        severity: 'medium',
        errorMessage: err?.message || 'Delete failed',
        component: 'ContentLayoutDetails',
        userAction: isVideoSelection ? 'Click delete on category video' : 'Click delete on album photo',
      });
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: `Oops! Something went wrong while removing ${selectionLabel.toLowerCase()}`,
      });
    } finally {
      setIsDeletingPhoto(false);
    }
  };

  // Get title based on type
  const getPageTitle = () => {
    if (groupType === '1') return layoutText?.categories || 'Categories';
    if (groupType === '2') return layoutText?.playlist || 'Playlists';
    return layoutText?.album || 'Photo Albums';
  };

  // Get icon based on type
  const getTypeIcon = (isSelected) => {
    if (groupType === '1') {
      return isSelected ? <FolderOpenIcon className={styles.menuIcon} /> : <FolderIcon className={styles.menuIcon} />;
    }
    if (groupType === '2') {
      return <PlaylistIcon className={styles.menuIcon} />;
    }
    return <PhotoIcon className={styles.menuIcon} />;
  };

  // Fetch sidebar items (categories/playlists/albums) with pagination.
  // preserveSelection: keep current selectedItem on first page (otherwise auto-select first item).
  // loadMore: true → append next page starting at current sidebarItems.length;
  //           false → fresh fetch starting at 0.
  const fetchSidebarItems = async (preserveSelection = false, loadMore = false) => {
    if (!groupType || !['1', '2', '3'].includes(groupType)) {
      setSidebarLoading(false);
      return;
    }

    if (loadMore) {
      // Synchronous ref guard (state check alone races the scroll firing).
      if (isSidebarLoadingMoreRef.current || !sidebarHasMore) return;
      isSidebarLoadingMoreRef.current = true;
      setSidebarLoadingMore(true);
    } else {
      isSidebarLoadingMoreRef.current = false;
      setSidebarLoading(true);
      setSidebarHasMore(true);
    }
    try {
      const endpoint = groupType === '1' ? endpoints.getMyCategory
        : groupType === '2' ? endpoints.getMyPlaylist
          : endpoints.getMyAlbum;

      const startOffset = loadMore ? sidebarItems.length : 0;
      // Albums sidebar shows newest-first (vpm: true → BE returns descending order_number).
      // Categories/playlists sidebar keep the original ascending order.
      const response = await dataFetcher(endpoint, {
        limit: SIDEBAR_PER_PAGE,
        start: startOffset,
        thumbnails: false,
        ...(groupType === '3' ? { vpm: true } : {}),
      });

      if (response?.success && Array.isArray(response?.data)) {
        const fetched = response.data;
        // "All Videos" is now freely reorderable, so honor the order the
        // backend returns instead of forcing the default category to top.
        const merged = loadMore ? [...sidebarItems, ...fetched] : fetched;
        setSidebarItems(merged);
        // No total count on these endpoints — infer end-of-list from a
        // short page (anything < limit means we hit the end).
        setSidebarHasMore(fetched.length === SIDEBAR_PER_PAGE);

        if (!loadMore) {
          // Preserve current selection or auto-select first item
          if (preserveSelection && selectedItem) {
            const updatedItem = merged.find((item) => item.id === selectedItem.id);
            if (updatedItem) {
              setSelectedItem(updatedItem);
            } else if (merged.length > 0) {
              setSelectedItem(merged[0]);
            }
          } else if (merged.length > 0) {
            setSelectedItem(merged[0]);
          }
        }
      } else if (loadMore) {
        // Empty/unsuccessful follow-up page — stop trying.
        setSidebarHasMore(false);
      }
    } catch (err) {
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Failed to load items',
      });
      if (loadMore) setSidebarHasMore(false);
    }
    if (loadMore) {
      isSidebarLoadingMoreRef.current = false;
      setSidebarLoadingMore(false);
    } else {
      setSidebarLoading(false);
    }
  };

  // Load sidebar items on mount (or when groupType switches)
  useEffect(() => {
    fetchSidebarItems();
  }, [groupType]);

  // The sidebar loads its whole list up front (SIDEBAR_PER_PAGE = 200), so
  // there is no scroll-based paging. The old optional scroll load-more was
  // removed — it misfired and isn't needed now the full list is fetched.

  // Playlists sidebar: fetch each playlist's first few videos (once per
  // playlist id) just for their thumbnails, to build the stacked preview.
  // Skipped for empty playlists — they keep the plain icon.
  useEffect(() => {
    if (groupType !== '2' || !sidebarItems.length) return;
    const toFetch = sidebarItems.filter((it) => {
      const count = it.video_count ?? it.videos_count ?? it.count ?? 0;
      return count > 0 && !playlistThumbsFetchedRef.current.has(it.id);
    });
    if (!toFetch.length) return;
    toFetch.forEach((it) => playlistThumbsFetchedRef.current.add(it.id));
    toFetch.forEach((it) => {
      dataFetcher(endpoints.getMyPlaylist, {
        playlist_id: it.id, vpm: false, limit: 3, start: 0,
      }).then((res) => {
        const itemData = res?.data?.[0] || res?.data;
        const videos = itemData?.videos || [];
        const thumbs = videos
          .map((v) => v.imagekit_icon || v.thumbnails?.[0] || v.thumbnail || null)
          .filter(Boolean)
          .slice(0, 3);
        if (thumbs.length) {
          setPlaylistStackThumbs((prev) => ({ ...prev, [it.id]: thumbs }));
        }
      }).catch(() => {
        // Silent — the row just keeps its icon fallback.
      });
    });
  }, [groupType, sidebarItems]);

  // Fetch content for selected item with infinite scroll support
  const fetchContentForItem = async (item, loadMore = false) => {
    if (!item) return;

    if (loadMore) {
      // Synchronous ref guard (state check alone races the scroll firing).
      if (isLoadingMoreRef.current || !hasMore) return;
      isLoadingMoreRef.current = true;
      setLoadingMore(true);
    } else {
      isLoadingMoreRef.current = false;
      startIsIgnoredRef.current = false;
      setContentLoading(true);
      setContentItems([]);
      setHasMore(true);
    }

    // One page request, plus the unwrap of the three different response shapes.
    // A closure rather than inline code because the retry below has to ask for a
    // different window without repeating the endpoint branching.
    const fetchPage = async (limit, start) => {
      const paginationParams = { limit, start };
      // Sort params are appended only when a non-default sort is active —
      // matches /innerpage behavior so the default layout order is preserved.
      const sortParams = sortBy !== 'layout'
        ? { sort_by: sortBy, sort_order: sortOrder }
        : {};

      let response;
      if (groupType === '1') {
        // Fetch videos for category using getCategoryDetail with pagination
        // vpm: false → BE returns videos in reversed (ascending order_number) order
        response = await dataFetcher(endpoints.getCategoryDetail, {
          user_id: userDetails?.myId,
          category_id: item.id,
          vpm: false,
          ...paginationParams,
          ...sortParams,
        });
      } else if (groupType === '2') {
        // Fetch videos for playlist (vpm: false → reversed, same as categories)
        response = await dataFetcher(endpoints.getMyPlaylist, {
          playlist_id: item.id,
          vpm: false,
          ...paginationParams,
          ...sortParams,
        });
      } else {
        // Fetch photos for album. getMyAlbumDetail uses a different param
        // shape than category/playlist: { sort_by: 'name', order_by: 0|1 }
        // where 0 → A-Z (ASC) and 1 → Z-A (DESC). Date sort is hidden in
        // the UI for albums, matching /your-videos albums tab.
        const albumSortParams = sortBy !== 'layout'
          ? { sort_by: sortBy, order_by: sortOrder === 'ASC' ? 0 : 1 }
          : {};
        response = await dataFetcher(endpoints.getMyAlbumDetail, {
          album_id: item.id,
          ...paginationParams,
          ...albumSortParams,
        });
      }

      if (!response?.success) return null;
      // getCategoryDetail returns videos/video_count directly;
      // getMyPlaylist and getMyAlbumDetail wrap theirs in a data array.
      if (groupType === '1') {
        return { items: response?.videos || [], total: response?.video_count || 0 };
      }
      const itemData = response.data?.[0] || response.data;
      if (groupType === '3') {
        return { items: itemData?.photos || [], total: itemData?.photo_count || 0 };
      }
      return { items: itemData?.videos || [], total: itemData?.video_count || 0 };
    };

    try {
      const startOffset = loadMore ? contentItems.length : 0;
      // Load the first TWO pages up front (48) when a category/album/playlist
      // opens, so content is there immediately instead of janking in on
      // scroll; subsequent scroll-loads page in one page (24) at a time.
      const requestedLimit = loadMore ? itemsPerPage : itemsPerPage * 2;

      // Identity, for dropping rows already on screen. Guards the two ways a
      // paginated endpoint can repeat itself — ignoring `start`, and rows
      // shifting between requests — and keeps the dnd-kit ids unique, which
      // duplicates would break.
      const identity = (it) => it?.id || it?.content_id || it?.video_id || it?.photo_id;
      const seen = new Set(contentItems.map(identity).filter(Boolean));
      const freshOnly = (items) => items.filter((it) => {
        const key = identity(it);
        return !key || !seen.has(key);
      });

      // An endpoint already proven to ignore `start` gets asked for the whole
      // range from 0; everything past what we hold is the new page.
      let askedFor = startIsIgnoredRef.current && loadMore
        ? startOffset + itemsPerPage
        : requestedLimit;
      let page = await fetchPage(askedFor, startIsIgnoredRef.current && loadMore ? 0 : startOffset);
      let appended = loadMore ? freshOnly(page?.items || []) : (page?.items || []);

      // A follow-up page holding nothing we don't already have means the server
      // answered with page one again — it takes `limit` but not `start`. Retry
      // once for the whole range from 0 and keep the tail. The verdict is
      // remembered for the rest of this album, so we probe at most once.
      if (loadMore && page && appended.length === 0 && !startIsIgnoredRef.current) {
        const wideLimit = startOffset + itemsPerPage;
        const wide = await fetchPage(wideLimit, 0);
        const wideFresh = freshOnly(wide?.items || []);
        if (wideFresh.length > 0) {
          startIsIgnoredRef.current = true;
          askedFor = wideLimit;
          page = wide;
          appended = wideFresh;
        }
      }

      if (!page) {
        // Unsuccessful follow-up page — stop trying, the same way the sidebar
        // does. Without this hasMore stays true and every further scroll
        // re-fires a request that has already said it has nothing to give.
        if (loadMore) setHasMore(false);
        return;
      }

      setTotalItems(page.total);

      if (loadMore) {
        setContentItems((prev) => [...prev, ...appended]);
      } else {
        setContentItems(page.items);
      }

      // Is there another page?
      //
      // `total` used to be the only signal, and for albums it lies:
      // getMyAlbumDetail reports `photo_count` for the SLICE it just returned,
      // not for the album, so a 1000-photo album answered the first request with
      // 48 photos and photo_count: 48 — `48 < 48` is false, hasMore flipped off,
      // and every album stopped dead at 48 however far you scrolled.
      //
      // So `total` is now only ever a reason to KEEP going, never the reason to
      // stop. What stops us is a short page — fewer rows than we asked for means
      // the end of the collection, whatever the count field claims — or a page
      // that added nothing new, which would otherwise loop forever.
      const loadedCount = loadMore ? contentItems.length + appended.length : appended.length;
      const pageWasFull = (page.items?.length || 0) >= askedFor;
      const totalSaysMore = page.total > loadedCount;
      setHasMore((!loadMore || appended.length > 0) && (pageWasFull || totalSaysMore));

      if (loadMore && appended.length === 0) {
        // Genuinely exhausted, or an endpoint that cannot page past what it has
        // already returned — from the UI those look identical (the list just
        // stops growing), so say which in the console.
        // eslint-disable-next-line no-console
        console.warn('[content paging] nothing new returned', {
          groupType,
          itemId: item?.id,
          start: startOffset,
          askedFor,
          returned: page.items?.length || 0,
          reportedTotal: page.total,
          alreadyLoaded: contentItems.length,
        });
      }
    } catch (err) {
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Failed to load content',
      });
    } finally {
      if (loadMore) {
        isLoadingMoreRef.current = false;
        setLoadingMore(false);
      } else {
        setContentLoading(false);
      }
    }
  };

  // Load content when selected item changes
  useEffect(() => {
    if (selectedItem) {
      fetchContentForItem(selectedItem);
    }
    // Clear photo selection whenever the user switches album/category in the sidebar.
    setSelectedPhotos([]);
    // The revert window is tied to the container that was just sorted — drop it
    // when the user navigates to a different item.
    if (revertVisible && revertParentIdRef.current !== selectedItem?.id) {
      hideRevert();
    }
  }, [selectedItem]);

  // Slide-in/out transition for the right-side photo selection panel.
  useEffect(() => {
    if (selectedPhotos.length > 0) {
      if (photosPanelTransitionState === 'exited') {
        setPhotosPanelTransitionState('entering');
        setTimeout(() => setPhotosPanelTransitionState('entered'), 50);
      }
    } else if (photosPanelTransitionState === 'entered') {
      setPhotosPanelTransitionState('exiting');
      setTimeout(() => setPhotosPanelTransitionState('exited'), 300);
    }
  }, [selectedPhotos.length, photosPanelTransitionState]);

  // Refetch when sort changes (categories, playlists, and albums). The
  // initial mount is harmless: selectedItem is null until the sidebar
  // loads, so the early-return below skips it; the selectedItem effect
  // handles the first fetch.
  useEffect(() => {
    if (!['1', '2', '3'].includes(groupType) || !selectedItem) return;
    setIsSorting(true);
    setHasMore(true);
    fetchContentForItem(selectedItem).finally(() => setIsSorting(false));
  }, [sortBy, sortOrder]);

  // Sort handler — matches /innerpage SortControls handleSort behavior.
  const handleSort = (value) => {
    const [type, orderValue] = value.split('-');
    if (type === 'layout') {
      if (sortBy === 'layout') return;
      setSortBy('layout');
      setSortOrder('DESC');
      return;
    }
    const newOrder = orderValue === '1' ? 'ASC' : 'DESC';
    if (sortBy === type && sortOrder === newOrder) return;
    setSortBy(type);
    setSortOrder(newOrder);
  };

  const handleClearSort = () => {
    if (sortBy === 'layout') return;
    setSortBy('layout');
    setSortOrder('DESC');
  };

  const sortValue = sortBy === 'layout'
    ? 'layout-0'
    : `${sortBy}-${sortOrder === 'ASC' ? '1' : '0'}`;

  // Map the UI sort state to the applyContentSort `sort_by` enum.
  // Returns null when the current sort can't be persisted (layout default).
  // NOTE: name/date keys are intentionally flipped — the BE treats them as the
  // opposite of their plain meaning (`name_desc` = A→Z, `date_oldest` = newest
  // first), so we send the inverse of the displayed sort direction here.
  const getApplySortKey = () => {
    if (sortBy === 'name') return sortOrder === 'ASC' ? 'name_asc' : 'name_desc';
    if (sortBy === 'date') return sortOrder === 'DESC' ? 'date_newest' : 'date_oldest';
    return null;
  };

  const handleApplyContentSort = async () => {
    const sortKey = getApplySortKey();
    if (!sortKey || !selectedItem?.id) return;
    setIsApplyingSort(true);
    try {
      const response = await dataFetcher(endpoints.applyContentSort, {
        type: groupType, // '1' for category — same as the route's :layoutType
        parent_id: selectedItem.id,
        sort_by: sortKey,
      });
      if (response?.success) {
        setAppSnackbar({ isVisible: true, message: 'Layout order updated' });
        setShowApplySortModal(false);
        // The saved layout order now matches the active sort. Drop back to
        // layout view so the user sees the persisted state and can drag-reorder.
        setSortBy('layout');
        setSortOrder('DESC');
        // Offer a one-shot revert for the next 30s (BE snapshotted the
        // previous order). Tied to the container we just sorted.
        startRevertCountdown(selectedItem.id);
      } else {
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: response?.message || 'Failed to update layout order',
        });
      }
    } catch (err) {
      logAppEvent({
        userName: `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
        functionAffected: 'Apply Content Sort',
        description: `applyContentSort failed for ${selectedItem?.id}`,
        severity: 'medium',
        errorMessage: err?.response?.data?.message || err?.message || 'applyContentSort failed',
        component: 'ContentLayoutDetails',
        userAction: 'Quick reorder confirm',
      });
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Failed to update layout order',
      });
    } finally {
      setIsApplyingSort(false);
    }
  };

  // One-shot revert of the most recent applyContentSort. Reuses the exact
  // type + encrypted parent_id from the sort call (token is auto-injected).
  const handleRevertContentSort = async () => {
    const parentId = revertParentIdRef.current || selectedItem?.id;
    if (!parentId || isReverting) return;
    setIsReverting(true);
    try {
      const response = await dataFetcher(endpoints.revertContentSort, {
        type: groupType,
        parent_id: parentId,
      });
      if (response?.success) {
        setAppSnackbar({ isVisible: true, message: 'Previous order restored' });
        hideRevert();
        // Snapshot is consumed — refresh the list so the restored order shows.
        setSortBy('layout');
        setSortOrder('DESC');
        if (selectedItem) {
          setHasMore(true);
          fetchContentForItem(selectedItem);
        }
      } else {
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: response?.message || 'Failed to revert order',
        });
      }
    } catch (err) {
      // 404 → nothing to undo (snapshot already consumed). Hide silently.
      if (err?.response?.status === 404) {
        hideRevert();
      } else {
        logAppEvent({
          userName: `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
          functionAffected: 'Revert Content Sort',
          description: `revertContentSort failed for ${parentId}`,
          severity: 'medium',
          errorMessage: err?.response?.data?.message || err?.message || 'revertContentSort failed',
          component: 'ContentLayoutDetails',
          userAction: 'Quick reorder revert',
        });
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: err?.response?.data?.message || 'Failed to revert order',
        });
      }
    } finally {
      setIsReverting(false);
    }
  };

  // Infinite scroll handler
  const handleScroll = React.useCallback(() => {
    const container = contentContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // Preload the next page ~600px before the bottom so it feels instant.
    if (scrollHeight - scrollTop - clientHeight < 600) {
      if (hasMore && !isLoadingMoreRef.current && !contentLoading && selectedItem) {
        fetchContentForItem(selectedItem, true);
      }
    }
    // contentItems.length in deps → the callback closes over the *current*
    // items so the next-page `start` offset is correct (was stale before).
  }, [hasMore, loadingMore, contentLoading, selectedItem, contentItems.length]);

  // Attach scroll listener
  useEffect(() => {
    const container = contentContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
    return undefined;
  }, [handleScroll]);

  // Second, independent load-more trigger: a sentinel at the bottom of the list.
  //
  // handleScroll above only ever fires if contentContainerRef is the element
  // that actually scrolls — one layout change upstream (or a viewport where the
  // page scrolls instead of the grid) and it goes silent, with the list simply
  // stopping at whatever the first page held. An observer with root: null keys
  // off real visibility instead, so it works whichever ancestor scrolls, and it
  // re-fires by itself when a freshly-appended page still leaves the sentinel in
  // view. Both paths share the isLoadingMoreRef guard, so they can't double-load.
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !hasMore || contentLoading || !selectedItem) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !isLoadingMoreRef.current) {
        fetchContentForItem(selectedItem, true);
      }
    }, { root: null, rootMargin: '600px 0px' });
    observer.observe(sentinel);
    return () => observer.disconnect();
    // contentItems.length → re-observe after each appended page, so the next
    // page is requested if the sentinel is still on screen.
  }, [hasMore, contentLoading, selectedItem, contentItems.length]);

  // Handle sidebar item click
  const handleSidebarItemClick = (item) => {
    // Prevent switching if sidebar is still loading
    if (sidebarLoading) {
      return;
    }

    // Prevent switching if content is still loading (showing shimmer)
    if (contentLoading) {
      return;
    }

    // Prevent switching if reorder queue is processing
    if (isSaving || isSidebarSaving) {
      setAppSnackbar({
        isVisible: true,
        type: 'warning',
        message: 'Please wait for reordering to complete'
      });
      return;
    }

    setSelectedItem(item);
    setHasMore(true);
    // Scroll to top when switching items
    if (contentContainerRef.current) {
      contentContainerRef.current.scrollTop = 0;
    }
    // Keep the selected item at the top of the sidebar.
    const idx = sidebarItems.findIndex((it) => it.id === item.id);
    if (idx >= 0 && sidebarListRef.current) {
      requestAnimationFrame(() => {
        const node = sidebarListRef.current?.children[idx];
        if (node && node.scrollIntoView) node.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    }
  };

  // Category selection is click-only. A previous "scroll over the sidebar to
  // step the selected category" wheel handler lived here but was buggy —
  // wheel ticks arriving during an in-flight per-category fetch were dropped
  // or raced, so it "sometimes worked, sometimes got stuck loading." Removed;
  // click-to-select (handleSidebarItemClick / onItemClick) and the grid's
  // within-category infinite scroll (handleScroll) are unaffected.

  // Sidebar per-item edit — select the item (if needed) then open the rename/image modal.
  const handleSidebarEditClick = (item) => {
    if (sidebarLoading || contentLoading || isSaving || isSidebarSaving) return;
    // Open the shared in-place edit modal for this sub-child (same one used on
    // the new /content-layout page) instead of the old editMode create modals.
    setLayoutEditItem(item);
  };

  // Grid search — don't filter (keep positions); scroll to the first match + highlight it.
  const runSearch = (q) => {
    setSearchQuery(q);
    const query = (q || '').trim().toLowerCase();
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    if (!query) { setHighlightId(null); return; }
    const idx = contentItems.findIndex((it) => (it.title || '').toLowerCase().includes(query));
    if (idx < 0) { setHighlightId(null); return; }
    const it = contentItems[idx];
    const id = it.id || it.content_id || it.video_id || it.photo_id || `item-${idx}`;
    setHighlightId(id);
    requestAnimationFrame(() => {
      const el = contentContainerRef.current?.querySelector(`[data-cardid="${id}"]`);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    highlightTimerRef.current = setTimeout(() => setHighlightId(null), 2600);
  };

  // The default "All Videos" category can't be reordered — drag, the sort
  // dropdown and Quick Reorder are all hidden for it. (Only relevant to
  // categories; playlists/albums have no such pseudo-item.)
  const isAllVideosCategory = groupType === '1' && selectedItem?.title === defaultCategoryName;

  // Get item ID for @dnd-kit - use item.id consistently for all types
  const getItemId = (item, idx) => (
    item.id || item.content_id || item.video_id || item.photo_id || `item-${idx}`
  );

  // @dnd-kit: Handle drag start
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    setIsDraggingContent(true); // Disable sidebar immediately when dragging starts
  };

  // Sequential queue processor: Process reorder operations one by one
  const processContentReorderQueue = React.useCallback(async () => {
    if (isProcessingQueueRef.current || reorderQueueRef.current.length === 0) {
      return;
    }

    isProcessingQueueRef.current = true;
    setIsSaving(true);

    const totalItems = reorderQueueRef.current.length;
    setQueueCount(totalItems);

    // Process each operation sequentially
    // eslint-disable-next-line no-await-in-loop
    while (reorderQueueRef.current.length > 0) {
      const operation = reorderQueueRef.current.shift(); // Get first item from queue
      setQueueCount(reorderQueueRef.current.length); // Update remaining count

      try {
        // BE precedence: after_id wins when both are sent, so only send one anchor.
        // before_id = "place item immediately before this item" (ascending order_number) —
        // needed when dropping at the bottom of a partially-loaded descending list.
        const payload = {
          type: operation.reorderType,
          item_id: operation.itemId,
          parent_id: operation.parentId,
        };
        if (operation.beforeId) {
          payload.before_id = operation.beforeId;
        } else {
          payload.after_id = operation.afterId;
        }
        // eslint-disable-next-line no-await-in-loop
        const response = await dataFetcher(endpoints.reorderItem, payload);

        if (!response.success) {
          setAppSnackbar({ isVisible: true, type: 'error', message: 'Failed to update order' });
          break; // Stop processing on error
        }
        // Ticks "Rearrange" off the dashboard getting-started checklist.
        markSectionExplored(EXPLORED_SECTION.rearrange);
      } catch (err) {
        logAppEvent({
          userName: `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
          functionAffected: 'Sequential Reorder Content',
          description: 'Content reorder failed',
          severity: 'medium',
          errorMessage: err?.message || 'Reorder failed',
          component: 'ContentLayoutDetails',
          userAction: 'Sequential queue processing',
        });
        setAppSnackbar({ isVisible: true, type: 'error', message: 'Failed to update order' });
        break; // Stop processing on error
      }
    }

    setQueueCount(0);
    setIsSaving(false);
    isProcessingQueueRef.current = false;
  }, [setAppSnackbar]);

  // Add reorder operation to queue
  const addToContentReorderQueue = React.useCallback((itemId, afterId, beforeId, parentId, reorderType) => {
    // Set saving state immediately to prevent sidebar from re-enabling between drag end and queue start
    setIsSaving(true);

    // Add to queue
    reorderQueueRef.current.push({ itemId, afterId, beforeId, parentId, reorderType });

    // Clear existing timer
    if (reorderTimerRef.current) {
      clearTimeout(reorderTimerRef.current);
    }

    // Start debounced queue processing
    reorderTimerRef.current = setTimeout(() => {
      processContentReorderQueue();
    }, DEBOUNCE_DELAY);
  }, [processContentReorderQueue]);

  // @dnd-kit: Handle drag end for reordering content
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    // Edge case: If dropped in same place or invalid drop, re-enable sidebar immediately
    if (!over || active.id === over.id) {
      setIsDraggingContent(false);
      return;
    }

    const oldIndex = contentItems.findIndex((item, idx) => getItemId(item, idx) === active.id);
    const newIndex = contentItems.findIndex((item, idx) => getItemId(item, idx) === over.id);

    // Edge case: Invalid indices, re-enable sidebar immediately
    if (oldIndex === -1 || newIndex === -1) {
      setIsDraggingContent(false);
      return;
    }

    const newItems = arrayMove(contentItems, oldIndex, newIndex);
    setContentItems(newItems);

    // Clear dragging flag - sidebar stays disabled during queue processing via isSaving state
    setIsDraggingContent(false);

    // Get the item that was moved and compute its anchor.
    // All groups now render ascending (order_number ASC): categories/playlists
    // via vpm: false, album photos natively. The ascending-order predecessor
    // (lower order_number) is therefore the item visually ABOVE the drop position.
    const movedItemId = active.id;
    const beforeId = null;
    const afterId = newIndex === 0 ? null : getItemId(newItems[newIndex - 1], newIndex - 1);

    // Determine the reorder type based on groupType
    const reorderType = groupType === '1' ? 'category_video'
      : groupType === '2' ? 'playlist_video'
        : 'album_photo';

    // Add to sequential queue
    addToContentReorderQueue(movedItemId, afterId, beforeId, selectedItem?.id, reorderType);
  };

  // Get active item for drag overlay
  const activeItem = activeId
    ? contentItems.find((item, idx) => getItemId(item, idx) === activeId)
    : null;

  // Sequential queue processor for sidebar: Process reorder operations one by one
  const processSidebarReorderQueue = React.useCallback(async () => {
    if (isSidebarProcessingQueueRef.current || sidebarReorderQueueRef.current.length === 0) {
      return;
    }

    isSidebarProcessingQueueRef.current = true;
    setIsSidebarSaving(true);

    const totalItems = sidebarReorderQueueRef.current.length;
    setSidebarQueueCount(totalItems);

    // Process each operation sequentially
    // eslint-disable-next-line no-await-in-loop
    while (sidebarReorderQueueRef.current.length > 0) {
      const operation = sidebarReorderQueueRef.current.shift(); // Get first item from queue
      setSidebarQueueCount(sidebarReorderQueueRef.current.length); // Update remaining count

      try {
        // Same anchor rule as the content queue: send exactly one of after_id/before_id.
        const payload = {
          type: operation.reorderType,
          item_id: operation.itemId,
        };
        if (operation.beforeId) {
          payload.before_id = operation.beforeId;
        } else {
          payload.after_id = operation.afterId;
        }
        // eslint-disable-next-line no-await-in-loop
        const response = await dataFetcher(endpoints.reorderItem, payload);

        if (!response.success) {
          setAppSnackbar({ isVisible: true, type: 'error', message: 'Failed to update sidebar order' });
          break; // Stop processing on error
        }
        // Ticks "Rearrange" off the dashboard getting-started checklist.
        markSectionExplored(EXPLORED_SECTION.rearrange);
      } catch (err) {
        logAppEvent({
          userName: `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
          functionAffected: 'Sequential Reorder Sidebar',
          description: 'Sidebar reorder failed',
          severity: 'medium',
          errorMessage: err?.message || 'Sidebar reorder failed',
          component: 'ContentLayoutDetails',
          userAction: 'Sequential sidebar queue processing',
        });
        setAppSnackbar({ isVisible: true, type: 'error', message: 'Failed to update sidebar order' });
        break; // Stop processing on error
      }
    }

    setSidebarQueueCount(0);
    setIsSidebarSaving(false);
    isSidebarProcessingQueueRef.current = false;
  }, [setAppSnackbar]);

  // Add sidebar reorder operation to queue
  const addToSidebarReorderQueue = React.useCallback((itemId, afterId, beforeId, reorderType) => {
    // Set saving state immediately to prevent sidebar from re-enabling between drag end and queue start
    setIsSidebarSaving(true);

    // Add to queue
    sidebarReorderQueueRef.current.push({ itemId, afterId, beforeId, reorderType });

    // Clear existing timer
    if (sidebarReorderTimerRef.current) {
      clearTimeout(sidebarReorderTimerRef.current);
    }

    // Start debounced queue processing
    sidebarReorderTimerRef.current = setTimeout(() => {
      processSidebarReorderQueue();
    }, DEBOUNCE_DELAY);
  }, [processSidebarReorderQueue]);

  // Sidebar reorder handlers (framer-motion). Keep a live mirror of sidebarItems so
  // the drag-end handler reads the settled order.
  useEffect(() => { sidebarItemsRef.current = sidebarItems; }, [sidebarItems]);

  const handleSidebarReorder = (nextItems) => {
    setSidebarItems(nextItems);
  };

  const handleSidebarItemDragStart = (itemId) => {
    draggingSidebarIdRef.current = itemId;
    draggingSidebarStartIndexRef.current = sidebarItemsRef.current.findIndex((item) => item.id === itemId);
    setIsDraggingSidebar(true); // Disable other sidebar items while dragging
  };

  const handleSidebarItemDragEnd = () => {
    const movedItemId = draggingSidebarIdRef.current;
    const items = sidebarItemsRef.current;
    const newIndex = items.findIndex((item) => item.id === movedItemId);
    const startIndex = draggingSidebarStartIndexRef.current;

    draggingSidebarIdRef.current = null;
    draggingSidebarStartIndexRef.current = -1;
    setIsDraggingSidebar(false);

    // Dropped in the same place or invalid — nothing to persist.
    if (newIndex === -1 || newIndex === startIndex) {
      return;
    }

    // Compute the moved item's anchor. Albums sidebar renders newest-first
    // (descending order_number via vpm: true), so the ascending-order predecessor is
    // the item visually BELOW the drop position. Categories/playlists render ascending.
    let afterId = null;
    let beforeId = null;
    if (groupType === '3') {
      const belowItem = items[newIndex + 1];
      if (belowItem) {
        afterId = belowItem.id;
      } else if (sidebarHasMore) {
        // Dropped at the bottom of the loaded page while older albums are still
        // unloaded — anchor on the item above instead: "place immediately before it".
        beforeId = items[newIndex - 1]?.id;
      }
      // else: dropped at the true bottom of the list → afterId stays null (first position)
    } else {
      afterId = newIndex === 0 ? null : items[newIndex - 1]?.id;
    }

    // Determine the reorder type based on groupType
    const reorderType = groupType === '1' ? 'category'
      : groupType === '2' ? 'playlist'
        : 'album';

    // Add to sequential queue
    addToSidebarReorderQueue(movedItemId, afterId, beforeId, reorderType);
  };

  // Delete selected item
  const { mutate: deleteItem, isValidating: isDeletingItem } = useSWR(
    showDeleteModal && selectedItem ? [
      groupType === '1' ? endpoints.deleteMyCategory
        : groupType === '2' ? endpoints.deleteMyPlayList
        : endpoints.deleteMyAlbum,
      selectedItem.id,
    ] : null,
    {
      fetcher: (url, id) => dataFetcher(url,
        groupType === '1' ? { catId: id }
          : groupType === '2' ? { id }
          : { album_id: id }),
      onSuccess: ({ success }) => {
        if (success) {
          setAppSnackbar({ isVisible: true, message: 'Deleted successfully' });
          setShowDeleteModal(false);
          setSelectedItem(null);
          fetchSidebarItems();
        } else {
          setAppSnackbar({ isVisible: true, type: 'error', message: 'Failed to delete' });
        }
      },
      onError: () => {
        setAppSnackbar({ isVisible: true, type: 'error', message: 'Failed to delete' });
      },
    }
  );

  // Get empty message
  const getEmptyMessage = () => {
    if (!selectedItem) {
      return `Select a ${groupType === '1' ? 'category' : groupType === '2' ? 'playlist' : 'album'} to view its contents`;
    }
    if (groupType === '3') {
      return 'No photos in this album';
    }
    return `No videos in this ${groupType === '1' ? 'category' : 'playlist'}`;
  };

  return (
    <V2ThemeProvider withBaseline={false}>
    <Page noHeader={!showHeader} slimV2 className={styles.page} contentClassName={cn(styles.pageContent, { [styles.pcHeaderPad]: showHeader })}>
      {/* Saving indicator */}
      {(isSaving || isSidebarSaving) && (
        <div className={styles.savingIndicator}>
          <CircularProgress size={14} style={{ color: 'white', marginRight: 8 }} />
          {queueCount > 0
            ? `Saving... (${queueCount} remaining)`
            : sidebarQueueCount > 0
              ? `Saving... (${sidebarQueueCount} remaining)`
              : 'Saving...'}
        </div>
      )}

      {/* The multi-select action pill renders inline in the content header
          (replacing the category name / mode switcher while items are selected)
          — see the .contentHeader title slot below. */}

      <div className={styles.mainLayout}>
        {/* Left Sidebar */}
        <div className={styles.leftSidebar}>
          {/* Sidebar Header */}
          <div className={styles.sidebarHeader}>
            {!hideBack && (
              <button className={styles.backButton} onClick={() => navigate(-1)}>
                <BackIcon style={{ width: 18, height: 18 }} />
                {commonText?.back || 'Back'}
              </button>
            )}
            <h2 className={styles.sidebarTitle}>{getPageTitle()}</h2>
          </div>

          {/* Sidebar Menu */}
          {sidebarLoading ? (
            <SidebarSkeleton />
          ) : (
            <Reorder.Group
              as="div"
              axis="y"
              values={sidebarItems}
              onReorder={handleSidebarReorder}
              className={styles.menuList}
              ref={sidebarListRef}
            >
              {sidebarItems.map((item) => {
                const isSelected = selectedItem?.id === item.id;
                const isPinned = item.title === defaultCategoryName;
                const isDisabled = sidebarLoading
                  || contentLoading
                  || isDraggingContent
                  || isSaving
                  || isSidebarSaving;

                return (
                  <SortableSidebarItem
                    key={item.id}
                    item={item}
                    isSelected={isSelected}
                    isPinned={isPinned}
                    onItemClick={handleSidebarItemClick}
                    onEdit={handleSidebarEditClick}
                    showEdit={sidebarEditable}
                    getTypeIcon={getTypeIcon}
                    groupType={groupType}
                    isDisabled={isDisabled}
                    onDragStart={handleSidebarItemDragStart}
                    onDragSettle={handleSidebarItemDragEnd}
                    stackedThumbs={playlistStackThumbs[item.id]}
                  />
                );
              })}

              {/* Single shimmer row matching the .menuItem pill shape
                  exactly (same padding, icon + label dimensions) so it
                  slots in as a believable placeholder for the next
                  category about to land. */}
              {sidebarLoadingMore && (
                <div className={styles.skeletonMenuItemPill} aria-hidden="true">
                  <div className={styles.skeletonIcon} />
                  <div className={styles.skeletonText} />
                </div>
              )}

              {/* Add button is hidden until the user has scrolled to
                  the end of the list — showing it mid-list invites
                  the user to add when there may still be existing
                  items waiting to load. */}
              {!sidebarHasMore && !sidebarLoadingMore && (
                <button
                  className={styles.addItemButton}
                  onClick={() => setShowCreateModal(true)}
                >
                  <AddIcon />
                  {groupType === '1' ? 'Add Category' : groupType === '2' ? 'Add Playlist' : 'Add Album'}
                </button>
              )}
            </Reorder.Group>
          )}
        </div>

        {/* Right Content Area */}
        <div className={styles.rightContent}>
          <div className={styles.stickyTop}>
            {/* "Download all" — its own row above the content header, same as
                the top-of-page control on /your-videos (not sharing a line
                with Search / view toggle / Reorder). */}
            {showDownloadAll && (
              <div className={styles.downloadAllRow}>
                <div ref={bulkDownloadControlRef} className={styles.downloadAllControl}>
                  <span
                    role="button"
                    tabIndex={0}
                    className={styles.downloadAllText}
                    onClick={!preparingBulkDownload ? () => setBulkDownloadMenuAnchor(bulkDownloadControlRef.current) : undefined}
                    onKeyDown={(e) => {
                      if (!preparingBulkDownload && e.key === 'Enter') setBulkDownloadMenuAnchor(bulkDownloadControlRef.current);
                    }}
                    style={{ cursor: preparingBulkDownload ? 'default' : 'pointer' }}
                  >
                    Download all your videos & photos
                  </span>
                  <Tooltip title={!preparingBulkDownload
                    ? 'Download all content'
                    : bulkDownloadProgress.total > 0
                      ? `Downloading ${bulkDownloadProgress.current}/${bulkDownloadProgress.total}`
                      : 'Downloading…'}
                  >
                    <span>
                      <button
                        type="button"
                        className={styles.downloadAllIconButton}
                        onClick={() => setBulkDownloadMenuAnchor(bulkDownloadControlRef.current)}
                        disabled={preparingBulkDownload}
                      >
                        {preparingBulkDownload
                          ? <CircularProgress size={18} style={{ color: '#5aa5ef' }} />
                          : <DownloadRoundedIcon fontSize="small" />}
                      </button>
                    </span>
                  </Tooltip>
                  <Menu
                    anchorEl={bulkDownloadMenuAnchor}
                    open={Boolean(bulkDownloadMenuAnchor)}
                    onClose={() => setBulkDownloadMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{ sx: { mt: 1, borderRadius: 2, minWidth: 220 } }}
                  >
                    {DOWNLOAD_MODES.map(({ mode, label, Icon }) => (
                      <MenuItem
                        key={mode}
                        onClick={() => { setBulkDownloadMenuAnchor(null); downloadAllContent(mode); }}
                        sx={{ fontSize: 14, fontWeight: 600, py: 1 }}
                      >
                        <ListItemIcon sx={{ minWidth: 30 }}><Icon size={17} /></ListItemIcon>
                        {label}
                      </MenuItem>
                    ))}
                  </Menu>
                </div>
              </div>
            )}
            {/* Content Header */}
            {(selectedItem || headerLeading) && (
            <div className={styles.contentHeader}>
              <div className={styles.titleWrap}>
                {/* When a leading element is supplied (the Your Content mode
                    switcher) it takes the title slot; otherwise show the
                    selected category/album as a pill with rename. */}
                {selectedPhotos.length > 0 ? (
                  <div className={styles.selectionPill}>
                    <span className={styles.selCount}>{`${selectedPhotos.length} selected`}</span>
                    <span className={styles.selDivider} />
                    <div className={styles.selActions}>
                      <PillAction
                        Icon={SelectAllIcon}
                        label={contentItems.length > 0 && selectedPhotos.length >= contentItems.length ? 'Deselect all' : 'Select all'}
                        onClick={() => setSelectedPhotos(
                          selectedPhotos.length >= contentItems.length ? [] : [...contentItems],
                        )}
                      />
                      {selectedPhotos.length === 1 && (
                        <PillAction Icon={AnimatedEdit} label="Edit" onClick={() => setEditItemTarget(selectedPhotos[0])} />
                      )}
                      <PillAction Icon={AnimatedDownload} label="Download" onClick={downloadSelected} />
                      <PillAction Icon={AnimatedShare} label="Share" onClick={shareSelected} />
                      <PillAction Icon={AnimatedTrash} label="Delete" onClick={requestBulkDelete} danger />
                    </div>
                    <button
                      type="button"
                      className={styles.selClose}
                      onClick={() => setSelectedPhotos([])}
                      aria-label="Clear selection"
                      title="Clear selection"
                    >
                      <CloseX size={18} strokeWidth={2.4} />
                    </button>
                  </div>
                ) : (headerLeading || (selectedItem && (
                  <>
                    <h2 className={styles.categoryPill}>{selectedItem.title}</h2>
                    {selectedItem.title !== defaultCategoryName && (
                      <>
                        <button
                          type="button"
                          className={styles.titleEditBtn}
                          onClick={() => handleSidebarEditClick(selectedItem)}
                          aria-label="Rename / change image"
                          title="Rename / change image"
                        >
                          <AnimatedEdit size={18} strokeWidth={2.2} />
                        </button>
                        <button
                          type="button"
                          className={styles.titleDeleteBtn}
                          onClick={() => setShowDeleteModal(true)}
                          aria-label={`Delete ${groupType === '1' ? 'category' : groupType === '2' ? 'playlist' : 'album'}`}
                          title={`Delete ${groupType === '1' ? 'category' : groupType === '2' ? 'playlist' : 'album'}`}
                        >
                          <AnimatedTrash size={18} strokeWidth={2.2} />
                        </button>
                      </>
                    )}
                  </>
                )))}
              </div>
              {/* While items are selected the pill takes the header — hide the
                  search / grid-list toggle / reorder so nothing collides. */}
              {selectedPhotos.length === 0 && (
              <div className={styles.headerRight}>
                <div className={styles.searchBox}>
                  <SearchIcon style={{ fontSize: 18, color: '#98a2b3' }} />
                  <input
                    className={styles.searchInput}
                    value={searchQuery}
                    onChange={(e) => runSearch(e.target.value)}
                    placeholder={`Search ${groupType === '3' ? 'photos' : 'videos'}…`}
                    aria-label="Search content"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className={styles.searchClear}
                      onClick={() => runSearch('')}
                      aria-label="Clear search"
                    >
                      <CancelIcon style={{ fontSize: 16 }} />
                    </button>
                  )}
                </div>
                <div className={styles.viewToggle} role="group" aria-label="View mode">
                  <button
                    type="button"
                    className={cn(styles.viewToggleBtn, {
                      [styles.viewToggleActive]: viewMode === 'grid',
                    })}
                    onClick={() => setViewMode('grid')}
                    aria-label="Grid view"
                    aria-pressed={viewMode === 'grid'}
                    title="Grid view"
                  >
                    <GridViewIcon style={{ fontSize: 18 }} />
                  </button>
                  <button
                    type="button"
                    className={cn(styles.viewToggleBtn, {
                      [styles.viewToggleActive]: viewMode === 'list',
                    })}
                    onClick={() => setViewMode('list')}
                    aria-label="List view"
                    aria-pressed={viewMode === 'list'}
                    title="List view"
                  >
                    <ListViewIcon style={{ fontSize: 18 }} />
                  </button>
                </div>
                {selectedItem && (
                  <>
                    <button
                      type="button"
                      className={styles.quickReorderChip}
                      onClick={(e) => setQuickSortAnchor(e.currentTarget)}
                      disabled={isSorting || contentLoading}
                      title="Quick reorder"
                      aria-label="Quick reorder"
                    >
                      <ArrowDownUp size={16} strokeWidth={2.2} />
                      <span>Reorder</span>
                    </button>
                    <Menu
                      anchorEl={quickSortAnchor}
                      open={Boolean(quickSortAnchor)}
                      onClose={() => setQuickSortAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      PaperProps={{ sx: { borderRadius: 2, mt: 0.5, minWidth: 220 } }}
                    >
                      <MenuItem sx={{ fontWeight: 600, fontSize: 13 }} onClick={() => { setQuickSortAnchor(null); handleSort('name-1'); }}>Name · A → Z</MenuItem>
                      <MenuItem sx={{ fontWeight: 600, fontSize: 13 }} onClick={() => { setQuickSortAnchor(null); handleSort('name-0'); }}>Name · Z → A</MenuItem>
                      {groupType !== '3' && [
                        <MenuItem key="dnew" sx={{ fontWeight: 600, fontSize: 13 }} onClick={() => { setQuickSortAnchor(null); handleSort('date-1'); }}>Date · Newest first</MenuItem>,
                        <MenuItem key="dold" sx={{ fontWeight: 600, fontSize: 13 }} onClick={() => { setQuickSortAnchor(null); handleSort('date-0'); }}>Date · Oldest first</MenuItem>,
                      ]}
                      <MenuItem sx={{ fontWeight: 600, fontSize: 13 }} onClick={() => { setQuickSortAnchor(null); handleSort('layout-0'); }}>Layout order</MenuItem>
                    </Menu>
                  </>
                )}
              </div>
              )}
            </div>
          )}

          {/* Save / Cancel — appear only when a reorder is pending (applied via the
              quick-reorder chip). Save persists the new order; Cancel reverts. */}
          {['1', '2', '3'].includes(groupType) && selectedItem && !isAllVideosCategory && sortBy !== 'layout' && (
            <div className={styles.orderActions}>
              <span className={styles.orderActionsLabel}>Order changed — save it?</span>
              <button
                type="button"
                className={styles.orderSave}
                onClick={() => setShowApplySortModal(true)}
                disabled={isSorting || contentLoading || isApplyingSort}
              >
                {isApplyingSort ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className={styles.orderCancel}
                onClick={handleClearSort}
                disabled={isApplyingSort}
              >
                Cancel
              </button>
            </div>
          )}
          </div>

          {/* Progressive loader just below the header while content streams in. */}
          {(contentLoading || loadingMore) && (
            <div className={styles.contentLoadingBar} aria-hidden="true">
              <span className={styles.contentLoadingBarFill} />
            </div>
          )}

          <Box
            ref={contentContainerRef}
            className={highlightId ? styles.searchDimming : undefined}
            sx={{
              position: 'relative',
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              // Dock clearance at the bottom of the scroll area.
              pb: '72px',
              // Small gutter (top + sides) so a highlighted card's glow renders
              // in the padding instead of being clipped at the scroll edge.
              pt: '6px',
              px: '8px',
              // Hide the scrollbar (still scrollable).
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {contentLoading ? (
              <div className={styles.loadingGrid}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            ) : contentItems.length > 0 ? (
              <>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={contentItems.map((item, idx) => getItemId(item, idx))}
                    strategy={viewMode === 'list' ? verticalListSortingStrategy : rectSortingStrategy}
                  >
                    <div className={viewMode === 'list' ? styles.contentList : styles.contentGrid}>
                      {contentItems.map((item, idx) => {
                        const itemId = getItemId(item, idx);
                        return (
                          <SortableItem
                            key={itemId}
                            id={itemId}
                            item={item}
                            groupType={groupType}
                            isAllVideos={isAllVideosCategory || disableReorder}
                            isSelected={isMultiSelectEnabled && isPhotoSelected(item.id)}
                            onToggleSelect={isMultiSelectEnabled ? togglePhotoSelection : undefined}
                            viewMode={viewMode}
                            isHighlighted={highlightId != null && itemId === highlightId}
                            onEdit={() => setEditItemTarget(item)}
                            onDelete={() => deleteItems([item])}
                            onDownload={() => downloadItems([item])}
                            canDelete={groupType !== '2'}
                          />
                        );
                      })}
                    </div>
                  </SortableContext>

                  {/* Drag Overlay - shows smooth dragging preview */}
                  <DragOverlay adjustScale style={{ transformOrigin: '0 0' }}>
                    {activeItem ? (
                      <ContentItemCard
                        item={activeItem}
                        groupType={groupType}
                        isDragging
                        isOverlay
                        viewMode={viewMode}
                      />
                    ) : null}
                  </DragOverlay>
                </DndContext>

                {/* Load-more sentinel — see the IntersectionObserver effect. */}
                {hasMore && <div ref={loadMoreSentinelRef} aria-hidden="true" style={{ height: 1 }} />}

                {/* Loading more indicator */}
                {loadingMore && (
                  <div className={styles.loadingMore}>
                    <CircularProgress size={24} />
                    <span>Loading more...</span>
                  </div>
                )}

                {/* End of list indicator */}
                {!hasMore && contentItems.length > 0 && (
                  <div className={styles.endOfList}>
                    {contentItems.length}
                    {' '}
                    {groupType === '3' ? 'photos' : 'videos'}
                    {' '}
                    loaded
                  </div>
                )}
              </>
            ) : (
              <div className={styles.emptyState}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  {groupType === '3' ? (
                    <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  ) : (
                    <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  )}
                </svg>
                <p>{getEmptyMessage()}</p>
              </div>
            )}
          </Box>
        </div>

        {/* (Selection actions now live in the floating pill at the top of the
            content area — see .selectionPill above.) */}
      </div>

      {/* Create Category Modal */}
      {showCreateModal && groupType === '1' && (
        <CreateCategoryPage
          initialOpen
          onModalClose={() => {
            setShowCreateModal(false);
            fetchSidebarItems();
          }}
        />
      )}

      {/* Create Playlist Modal */}
      {showCreateModal && groupType === '2' && (
        <AddPlaylistModal
          open
          onClose={() => setShowCreateModal(false)}
          onPlaylistCreated={() => {
            setShowCreateModal(false);
            fetchSidebarItems();
          }}
        />
      )}

      {/* Create Album Modal */}
      {showCreateModal && groupType === '3' && (
        <AddAlbumModal
          open
          onClose={() => setShowCreateModal(false)}
          onAlbumCreated={() => {
            setShowCreateModal(false);
            fetchSidebarItems();
          }}
        />
      )}

      {/* Delete Confirmation Modal — same dialog styling as the bulk
          photo/video delete confirm below; title/button text unchanged. */}
      <Dialog
        open={showDeleteModal}
        TransitionComponent={Zoom}
        keepMounted
        aria-labelledby="delete-item-dialog-title"
        maxWidth="xs"
        fullWidth
        onClose={() => { if (!isDeletingItem) setShowDeleteModal(false); }}
        PaperProps={{ className: photoDeleteStyles.dialogPaper }}
      >
        <div className={photoDeleteStyles.dialogContainer}>
          <div className={photoDeleteStyles.iconWrap}>
            <DeleteOutlineIcon />
          </div>
          <h3 id="delete-item-dialog-title" className={photoDeleteStyles.dialogTitle}>
            Are you sure you want to delete?
          </h3>
          <div className={photoDeleteStyles.actionContainer}>
            <button
              type="button"
              className={photoDeleteStyles.cancelBtn}
              onClick={() => setShowDeleteModal(false)}
              disabled={isDeletingItem}
            >
              Go back
            </button>
            <button
              type="button"
              className={photoDeleteStyles.deleteBtn}
              onClick={deleteItem}
              disabled={isDeletingItem}
            >
              Delete
            </button>
          </div>
        </div>
      </Dialog>

      {/* Quick Reorder Confirmation Modal */}
      <Dialog
        open={showApplySortModal}
        TransitionComponent={Zoom}
        onClose={() => { if (!isApplyingSort) setShowApplySortModal(false); }}
        maxWidth="sm"
        fullWidth
        className={styles.applySortDialog}
      >
        <div className={styles.applySortHeader}>
          <div className={styles.applySortIconWrap}>
            <LayoutIcon className={styles.applySortIcon} />
          </div>
          <h2 className={styles.applySortTitle}>
            Save as Default Layout?
          </h2>
        </div>
        <div className={styles.applySortBody}>
          The current sort will become the saved order for
          {' '}
          <strong>{selectedItem?.title}</strong>
          . You can still drag-and-drop to fine-tune it afterwards.
          <div className={styles.applySortSummary}>
            {sortBy === 'name' && <TextIcon style={{ fontSize: 22, color: '#666' }} />}
            {sortBy === 'date' && <DateIcon style={{ fontSize: 22, color: '#666' }} />}
            <span>
              {sortBy === 'name'
                ? (sortOrder === 'ASC' ? 'Name (A-Z)' : 'Name (Z-A)')
                : 'Date Published'}
            </span>
            {sortBy === 'name' && (sortOrder === 'ASC'
              ? <ArrowUpward style={{ fontSize: 20, color: '#4CAF50' }} />
              : <ArrowDownward style={{ fontSize: 20, color: '#FF9800' }} />)}
            {sortBy === 'date' && (sortOrder === 'DESC'
              ? <ArrowUpward style={{ fontSize: 20, color: '#4CAF50' }} />
              : <ArrowDownward style={{ fontSize: 20, color: '#FF9800' }} />)}
          </div>
        </div>
        <div className={styles.applySortActions}>
          <button
            type="button"
            className={styles.applySortCancel}
            onClick={() => setShowApplySortModal(false)}
            disabled={isApplyingSort}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.applySortConfirm}
            onClick={handleApplyContentSort}
            disabled={isApplyingSort}
          >
            {isApplyingSort && <CircularProgress size={16} style={{ color: '#fff' }} />}
            {isApplyingSort ? 'Applying...' : 'Apply'}
          </button>
        </div>
      </Dialog>

      {/* Edit Category Modal - reuses CreateCategoryPage in edit mode */}
      {showEditModal && groupType === '1' && selectedItem && (
        <CreateCategoryPage
          initialOpen
          editMode
          editData={{
            id: selectedItem.id,
            title: selectedItem.title,
            icon: selectedItem.icon_id || selectedItem.icon,
          }}
          onModalClose={() => {
            setShowEditModal(false);
            fetchSidebarItems(true); // Preserve selection after edit
          }}
        />
      )}

      {/* Edit Playlist Modal */}
      {showEditModal && groupType === '2' && selectedItem && (
        <AddPlaylistModal
          open
          editMode
          editData={{
            id: selectedItem.id,
            title: selectedItem.title,
          }}
          onClose={() => setShowEditModal(false)}
          onPlaylistCreated={() => {
            setShowEditModal(false);
            fetchSidebarItems(true); // Preserve selection after edit
          }}
        />
      )}

      {/* Edit Album Modal */}
      {showEditModal && groupType === '3' && selectedItem && (
        <AddAlbumModal
          open
          editMode
          editData={{
            id: selectedItem.id,
            title: selectedItem.title,
          }}
          onClose={() => setShowEditModal(false)}
          onAlbumCreated={() => {
            setShowEditModal(false);
            fetchSidebarItems(true); // Preserve selection after edit
          }}
        />
      )}

      {/* Bulk Delete Confirmation Dialog — shared by album photos and
          category videos; labels and copy adapt to the selection type. */}
      <Dialog
        open={deleteCount > 0}
        TransitionComponent={Zoom}
        keepMounted
        aria-labelledby="delete-photo-dialog-title"
        aria-describedby="delete-photo-dialog-description"
        maxWidth="xs"
        fullWidth
        onClose={handleCancelPhotoDelete}
        PaperProps={{ className: photoDeleteStyles.dialogPaper }}
      >
        <div className={photoDeleteStyles.dialogContainer}>
          <div className={photoDeleteStyles.iconWrap}>
            <DeleteOutlineIcon />
          </div>
          <h3 id="delete-photo-dialog-title" className={photoDeleteStyles.dialogTitle}>
            {deleteCount > 1
              ? `Delete ${deleteCount} ${selectionLabelPlural}?`
              : `Delete ${selectionLabel}?`}
          </h3>
          <p id="delete-photo-dialog-description" className={photoDeleteStyles.dialogMessage}>
            {deleteCount > 1
              ? `Are you sure you want to delete these ${deleteCount} ${selectionLabelPlural.toLowerCase()}?`
              : (firstDeleteCandidate?.title
                ? `Are you sure you want to delete "${firstDeleteCandidate.title}"?`
                : `Are you sure you want to delete this ${selectionLabel.toLowerCase()}?`)}
            <br />
            This action cannot be undone.
          </p>
          <div className={photoDeleteStyles.actionContainer}>
            <button
              type="button"
              className={photoDeleteStyles.cancelBtn}
              onClick={handleCancelPhotoDelete}
              disabled={isDeletingPhoto}
            >
              Cancel
            </button>
            <button
              type="button"
              className={photoDeleteStyles.deleteBtn}
              onClick={handleConfirmPhotoDelete}
              disabled={isDeletingPhoto}
            >
              {isDeletingPhoto
                ? 'Deleting...'
                : (deleteCount > 1
                  ? `Delete ${deleteCount} ${selectionLabelPlural}`
                  : 'Delete')}
            </button>
          </div>
        </div>
      </Dialog>
      <ShareItemModal
        isVisible={shareModalOpen}
        url={shareUrl}
        loading={shareLoading}
        revoking={shareRevoking}
        onRevoke={handleRevokeShare}
        onClose={() => setShareModalOpen(false)}
      />
      {/* Single-file edit — from a card's Edit action or the pill's Edit (1 item) */}
      {editItemTarget && groupType !== '3' && (
        <VideoEditModal
          open
          onClose={() => setEditItemTarget(null)}
          videoId={editItemTarget?.id}
          onSaveSuccess={() => { setEditItemTarget(null); fetchContentForItem(selectedItem); }}
          // Its Download button routes through the parent, same as /your-videos
          onDownloadVideos={downloadItems}
        />
      )}
      {editItemTarget && groupType === '3' && (
        <PhotoEditModal
          open
          selectedItems={[editItemTarget]}
          contentDetails={selectedItem}
          onClose={() => { setEditItemTarget(null); fetchContentForItem(selectedItem); }}
          // Its "Download" button downloads the whole album (folder picker), the
          // same handler the old albums tab passed in.
          onDownloadAlbums={downloadAlbums}
        />
      )}

      {/* Shared in-place edit modal for the selected sub-child (name + image +
          reorder grid) — same one used on the new /content-layout page. */}
      <CategoryEditModal
        open={Boolean(layoutEditItem)}
        item={layoutEditItem}
        section={layoutEditItem ? {
          reorderType: groupType === '1' ? 'category' : groupType === '2' ? 'playlist' : 'album',
          singular: groupType === '1' ? 'category' : groupType === '2' ? 'playlist' : 'album',
          title: groupType === '1' ? 'Categories' : groupType === '2' ? 'Playlists' : 'Photo Albums',
          key: groupType === '1' ? 'categories' : groupType === '2' ? 'playlists' : 'albums',
          layoutType: groupType,
        } : null}
        userId={userDetails?.myId}
        onClose={() => setLayoutEditItem(null)}
        onSaved={({ id, title, icon }) => {
          setSidebarItems((prev) => prev.map((it) => (it.id === id
            ? { ...it, title, icon: icon || it.icon, imagekit_icon: icon || it.imagekit_icon }
            : it)));
          setSelectedItem((cur) => (cur && cur.id === id
            ? { ...cur, title, icon: icon || cur.icon, imagekit_icon: icon || cur.imagekit_icon }
            : cur));
        }}
      />
      <SaveIndicator />
      <LivePreviewPop />
    </Page>
    </V2ThemeProvider>
  );
};
